import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { withBrandSuffix, withBrandDesc, withBrandKeywords } from '@/lib/seoBrand';

// Single-domain SEO strategy:
//   japantollreceipts.jp/...      → Japanese pages (canonical for `ja`, x-default)
//   japantollreceipts.jp/en/...   → English pages (canonical for `en`)
//
// Any legacy alternate-TLD alias (e.g. an external GoDaddy redirect)
// is handled at DNS / 301 layer and is never referenced from this app:
// no canonical, no hreflang alternate, and no sitemap entry ever points
// at an alternate-TLD alias.
const DOMAIN_JP = 'https://japantollreceipts.jp';
const OG_IMAGE = '/brand/jtr-og-homepage-1200x630.jpg';

/** Returns the single active brand domain. Kept as a function so future
 *  domain logic (e.g. preview-only canonical fallbacks) has a single
 *  insertion point. */
function _activeBrandDomain() {
  return DOMAIN_JP;
}

const SEO_DATA = {
  // ── Homepage — primary brand + service keyword anchors ──────────
  '/': {
    ja: {
      title: 'ETC利用明細・ETC領収書をメール自動配信｜ETC利用照会サービス連携｜Japan Toll Receipts',
      desc: 'ETC利用明細・ETC利用履歴・ETC領収書（利用証明書）を、ETC利用照会サービス（旧称 MEISAI）と連携して日次・週次・月次のメールで自動受信。ETCカード最大10枚対応、PDF・CSV出力、ETCマイレージサービス・ETC割引・ETC車載器管理番号・ETCコーポレートカード・法人ETCカード・社用車ETC・営業車ETC・レンタカーETC・カーシェアETCの利用記録整理にも対応。高速料金検索・高速料金計算後に必要となる「実際の利用記録」を、毎回ログインせず自動で取得・整理・配信。経費精算・確定申告・インボイス制度対応。1年間プレミアム無料体験（クレジットカード登録不要）。',
      keywords: 'ETC利用明細,ETC利用照会サービス,ETC領収書,ETC利用証明書,ETC利用履歴,ETCカード,ETCカード利用明細,ETCカード領収書,ETCマイレージサービス,ETCマイレージ,ETCマイレージサービスログイン,ETCマイレージ登録,ETCマイレージポイント,高速道路料金,高速料金,高速料金検索,高速料金計算,高速道路料金検索,高速代,高速代検索,高速道路利用明細,高速道路領収書,高速道路利用証明書,ETC割引,ETC休日割引,ETC深夜割引,ETC料金,ETC車載器,ETC車載器管理番号,ETC再セットアップ,ETCセットアップ証明書,法人ETCカード,ETCコーポレートカード,社用車ETC,営業車ETC,ETCカードレンタカー,レンタカーETC領収書,レンタカーETC利用明細,カーシェアETC,タイムズカーETC,ETC経費精算,ETC仕訳,ETC勘定科目,ETCインボイス,ETC利用照会サービス使い方,ETC利用照会サービス登録,ETC利用照会サービスログイン,ETC明細PDF,ETC明細CSV,ETC明細メール自動配信,MEISAI',
    },
    en: {
      title: 'Japan Toll Receipts | Automated ETC Receipts by Email — ETC Usage Inquiry Service Connected',
      desc: 'Receive Japan ETC toll receipts, usage statements (ETC利用明細), usage certificates (ETC利用証明書), and CSV reimbursement files by email — daily, weekly, or monthly. Connects to the official ETC Usage Inquiry Service (ETC利用照会サービス / MEISAI). Supports up to 10 ETC cards per account, business ETC card / corporate ETC card fleets, rental-car and carshare ETC, ETC discounts (休日割引・深夜割引), ETC Mileage Service record organization, OBU management number lookup, expense reports, qualified-invoice retention, and bilingual JA + EN delivery. 1-year premium free trial, no credit card.',
      keywords: 'Japan toll receipts,Japan ETC receipt,Japan ETC usage statement,Japan ETC usage certificate,ETC Usage Inquiry Service,ETC利用照会サービス,MEISAI receipts,ETC Mileage Service,ETC mileage points,Japan highway toll fee,Japan highway fee search,Japan toll fee calculator,Japan ETC card,Japan corporate ETC card,Japan business ETC card,Japan fleet ETC management,Japan rental car ETC,Japan carshare ETC,Japan ETC discount,Japan ETC night discount,Japan ETC weekend discount,Japan ETC OBU management number,ETC setup certificate,how to get ETC receipt Japan,Japan toll PDF CSV,Japan toll expense report,Japan qualified invoice ETC,foreign resident ETC Japan',
    },
  },

  // ── Founder's Message — narrative + credibility + SEO ───────────
  '/founders-message': {
    ja: {
      title: '創業者からのメッセージ｜なぜ JapanTollReceipts.jp を創ったのか | JTR',
      desc: '創業者アダム・ジョーンズからのメッセージ。日本のETC請求の遅れ、自動通行料レシート、ETC利用照会サービス（MEISAI） レポート、そしてETC専用化が進むなかでの複数車両 コンプライアンス課題 ―― JapanTollReceipts.jp が生まれた理由をお伝えします。',
      keywords: 'JapanTollReceipts.jp,JapanETCcard.com,創業者メッセージ,ETC専用化,車両管理,法人ETCコンプライアンス,官公庁ETCコンプライアンス,公共部門ETC,ETC明細,ETC利用照会サービス（MEISAI）,ETCマイレージ登録,通行料 経費レポート,ETC利用明細 メール,自動 ETC レシート',
    },
    en: {
      title: 'Founder\'s Message | Why JapanTollReceipts.jp Was Created for ETC Receipts, ETC Usage Inquiry Service (MEISAI) Reports & Fleet Compliance',
      desc: 'A message from founder Adam Jones on why JapanTollReceipts.jp was created to solve delayed ETC billing, missing toll receipts, ETC Usage Inquiry Service (MEISAI) reporting challenges, fleet compliance, and Japan\'s ETC-only future.',
      keywords: 'JapanTollReceipts.jp,JapanETCcard.com,Japan toll receipts,ETC toll receipts Japan,ETC Usage Inquiry Service (MEISAI) receipts,MEISAI reports,ETC usage certificate,ETC card reports,ETC billing delay,Japan ETC billing,ETC-only gates Japan,fleet ETC management,government ETC compliance,public-sector ETC card oversight,business toll reports Japan,toll expense reports Japan,ETC mileage registration',
    },
  },
  // Alias entry for the legacy /our-story URL so SEO meta tags still
  // update correctly when visitors land there. Same content as
  // /founders-message — point inbound links at /founders-message for
  // canonical purposes.
  '/our-story': {
    ja: {
      title: '創業者からのメッセージ｜なぜ JapanTollReceipts.jp を創ったのか | JTR',
      desc: '創業者アダム・ジョーンズからのメッセージ。日本のETC請求の遅れ、自動通行料レシート、ETC利用照会サービス（MEISAI） レポート、そしてETC専用化が進むなかでの複数車両 コンプライアンス課題 ―― JapanTollReceipts.jp が生まれた理由をお伝えします。',
      keywords: 'JapanTollReceipts.jp,JapanETCcard.com,創業者メッセージ,ETC専用化,車両管理,法人ETCコンプライアンス,官公庁ETCコンプライアンス,公共部門ETC,ETC明細,ETC利用照会サービス（MEISAI）,ETCマイレージ登録,通行料 経費レポート,ETC利用明細 メール,自動 ETC レシート',
    },
    en: {
      title: 'Founder\'s Message | Why JapanTollReceipts.jp Was Created for ETC Receipts, ETC Usage Inquiry Service (MEISAI) Reports & Fleet Compliance',
      desc: 'A message from founder Adam Jones on why JapanTollReceipts.jp was created to solve delayed ETC billing, missing toll receipts, ETC Usage Inquiry Service (MEISAI) reporting challenges, fleet compliance, and Japan\'s ETC-only future.',
      keywords: 'JapanTollReceipts.jp,JapanETCcard.com,Japan toll receipts,ETC toll receipts Japan,ETC Usage Inquiry Service (MEISAI) receipts,MEISAI reports,ETC usage certificate,ETC card reports,ETC billing delay,Japan ETC billing,ETC-only gates Japan,fleet ETC management,government ETC compliance,public-sector ETC card oversight,business toll reports Japan,toll expense reports Japan,ETC mileage registration',
    },
  },

  // ── Pricing — high-intent commercial query ──────────────────────
  '/pricing': {
    ja: {
      title: '料金プラン｜無料週次レポート・Daily Plus（1枚目300円＋追加250円）',
      desc: 'ETC利用履歴の自動配信プラン。Free Weeklyは継続して無料。個人のDaily Plusは追跡するETCカードの枚数で課金（1枚目300円＋追加カード250円・最大10枚・税抜）。法人・官公庁は月額1,980円で5枚込み＋6〜500枚250円／501枚〜200円（最大60,000枚）。1年間プレミアム無料体験付き、クレジットカード登録不要。',
      keywords: 'ETC料金プラン,ETC明細 料金,Daily Plus 300円,ETC明細 月額,ETCカード 枚数 料金,ETC利用明細 無料,ETC利用履歴 無料,1年間無料体験,ETCレポート 価格,法人ETC 料金,社用車ETC 料金',
    },
    en: {
      title: 'Pricing | Free Weekly & Daily Plus (per ETC card) — Japan Toll Receipts',
      desc: 'Compare plans: Free Weekly (¥0) and Daily Plus, priced per tracked ETC card. Personal: ¥300 first card + ¥250 each additional (up to 10). Business/Government: ¥1,980 includes 5 cards, +¥250 (6–500), +¥200 (501+), up to 60,000. 1-year premium free trial. No credit card to start.',
      keywords: 'Japan toll receipts pricing,ETC report price Japan,Daily Plus pricing,per ETC card pricing Japan,1-year free trial ETC Japan,Japan toll receipt cost',
    },
  },

  // ── How It Works — informational + intent-driven ────────────────
  '/architecture': {
    ja: {
      title: '当社のエンジニアリング構成｜システム構成と安全設計の技術解説',
      desc: 'JapanTollReceiptsがETC利用照会サービス（MEISAI）とどのように連携して動作するのか、その技術解説。一元化された制御プレーン、単一の安全ガバナー、非公開ワーカーによる制御された安全でスケーラブルな処理を層ごとにご説明します。',
      keywords: 'JapanTollReceipts システム構成,ETC利用照会サービス 連携 仕組み,安全ガバナー,キュー型処理,スケーラブル ETC自動化',
    },
    en: {
      title: 'Our Engineering Architecture | System Design & Safety Model',
      desc: 'A technical explanation of how Japan Toll Receipts works with the ETC Usage Inquiry Service (MEISAI): a centralized control plane, a single safety governor, and private worker processes that deliver controlled, safe, and scalable report processing.',
      keywords: 'JapanTollReceipts architecture,ETC Usage Inquiry Service (MEISAI) integration,safety governor,queue-based processing,scalable ETC automation',
    },
  },

  // ── How It Works ─────────────────────────────────────────────────
  '/how-it-works': {
    ja: {
      title: '使い方｜ETC利用明細をメールで受け取る5つのステップ',
      desc: 'アカウント登録、メール認証、ETC利用照会サービス（MEISAI）接続、ETCカード登録、配信スケジュール設定までを5ステップで完了。ETC利用明細の自動メール配信を最短10分で開始でき、PDF・CSV形式で経費精算・確定申告にも活用できます。日本語・英語のサポート対応。',
      keywords: 'ETC利用明細 設定方法,MEISAI 連携 方法,ETCカード 登録,ETC利用照会サービス 使い方,ETC自動配信 設定,ETCレポート メール 設定',
    },
    en: {
      title: 'How It Works | Set Up Automated Japan ETC Toll Receipts',
      desc: 'Five steps to start receiving Japan ETC toll receipts by email: register, verify, connect ETC Usage Inquiry Service (MEISAI), add ETC cards, choose your schedule. Setup takes ~10 minutes. Bilingual JA + EN support throughout.',
      keywords: 'how to set up ETC receipts Japan,ETC Usage Inquiry Service (MEISAI) connection guide,ETC card setup Japan,Japan toll receipt automation guide',
    },
  },

  // ── Free Weekly Reports — primary acquisition page ──────────────
  '/free-weekly-reports': {
    ja: {
      title: '無料ETC利用明細 週次レポート｜1年間のプレミアム無料体験付き',
      desc: 'ETC利用明細を毎週日曜日の朝にメールで無料受信。さらに1年間Daily Plus全機能を無料体験。クレジットカード登録不要。試用期間終了後はFree Weeklyへ自動移行で継続して無料。',
      keywords: 'ETC無料レポート,週次ETC利用明細,ETC利用明細 メール 無料,ETC明細 自動配信 無料,ETC週次レポート 無料',
    },
    en: {
      title: 'Free Weekly ETC Toll Reports + 1-Year Premium Trial — Japan Toll Receipts',
      desc: 'Free weekly ETC toll receipt emails — every Sunday morning, JST. Plus 1 year of Daily Plus features at no charge. No credit card. After the trial, accounts auto-revert to Free Weekly forever.',
      keywords: 'free ETC toll receipts Japan,free weekly toll report Japan,no credit card ETC report,free Japan toll receipt service',
    },
  },

  // ── Free Weekly Plan — dedicated marketing surface (always free) ─
  '/free-weekly': {
    ja: {
      title: 'Free Weekly プラン｜継続して無料・最大10枚まで・週次レポート',
      desc: 'Japan Toll Receipts の Free Weekly プランは継続して無料。クレジットカード登録不要。ETCカード最大10枚まで、毎週月曜の朝に利用明細PDFとCSVをメールで自動受信。1年間プレミアム体験終了後も自動的にこのプランへ移行し、サービスは止まりません。',
      keywords: 'ETC無料プラン,Free Weekly 継続して無料,ETC週次レポート 無料,ETC明細 無料 メール,クレジットカード登録不要 ETC,ETCカード 10枚 無料,日本ETC 無料サービス',
    },
    en: {
      title: 'Free Weekly Plan | Always Free, Up to 10 ETC Cards — Japan Toll Receipts',
      desc: 'The Free Weekly plan from Japan Toll Receipts stays free forever. No credit card. Up to 10 ETC cards. Two PDFs + CSV delivered every Monday morning. Even after the 1-year Premium trial ends, accounts auto-revert to Free Weekly so your service never disappears.',
      keywords: 'free ETC plan Japan,Japan toll receipt free forever,no credit card toll service Japan,free weekly ETC report Japan,10 ETC cards free plan',
    },
  },

  // ── Free Trial — high-conversion landing ────────────────────────
  '/free-trial': {
    ja: {
      title: '1年間のプレミアム無料体験｜Daily Plus全機能・クレジットカード登録不要',
      desc: '新規ご登録で1年間Daily Plusの対象機能を無料体験。日次・週次・月次・個別レポート、ETCカード10枚、PDF・CSV・利用証明書配信、車両別レポート、登録済み車両モニタリングまで全機能をお試しいただけます。クレジットカード登録不要で開始でき、終了後はFree Weeklyへ自動移行します。',
      keywords: '1年間無料体験,ETC無料体験,Daily Plus 無料,ETC利用明細 無料体験,プレミアム無料 ETC,クレジットカード登録不要 ETC',
    },
    en: {
      title: '1-Year Premium Free Trial | Japan Toll Receipts',
      desc: 'Every new account gets 1 full year of Daily Plus free — daily, weekly, monthly, and on-demand reports, 10 ETC cards, PDF + CSV + certificate attachments. No credit card required to activate.',
      keywords: '1-year free trial Japan toll receipts,ETC free trial Japan,Daily Plus trial,Japan toll report free trial',
    },
  },

  // ── Early Access waitlist (pre-launch email capture) ───────────
  '/early-access': {
    ja: {
      title: '先行受付登録｜ローンチ前に1年間プレミアム無料トライアルを確保',
      desc: 'ETC利用明細レポートを全自動で。公開ローンチ前に先行受付枠を確保した方には、1年間のプレミアム無料トライアルを差し上げます。アカウント作成は不要、メールアドレスだけで登録できます。クレジットカード登録不要。いつでも配信停止可能。Japan Toll Receipts は独立したサービスであり、NEXCO・ETC利用照会サービス（MEISAI）ではありません。',
      keywords: 'ETC 先行受付,Japan Toll Receipts 先行受付,ETC利用明細 待機リスト,ETC 1年間無料トライアル,ETC レポート 先行登録,ETC利用照会サービス 自動',
    },
    en: {
      title: 'Early Access Signup | Reserve Your 1-Year Premium Trial Before Launch',
      desc: 'Automated ETC toll usage reports. Reserve an Early Access spot before public launch and get a 1-Year Premium Trial. No account needed — just your email. No credit card. Unsubscribe anytime. Japan Toll Receipts is independent and is not NEXCO or the ETC Usage Inquiry Service (MEISAI).',
      keywords: 'Japan Toll Receipts early access,ETC report waitlist,ETC 1-year free trial,early access signup Japan ETC,pre-launch ETC automation',
    },
  },
  // ── Ad / campaign landing pages (conversion-first, still indexable) ──
  // General + 3 audience-targeted variants. All feed the same Early Access
  // waitlist; each targets a distinct long-tail keyword cluster.
  '/early-access-ad': {
    ja: {
      title: 'ETC管理を自動化｜先行受付登録（1年間プレミアム無料）',
      desc: 'ETCカード・車両・通行料金データを一元管理。ETC利用明細を自動取得し、毎日のレシートをPDF・CSVで自動配信。個人・法人・官公庁に対応。先行受付枠（先着100,000名）で1年間プレミアム無料トライアル。メールアドレスだけで登録、クレジットカード不要。Japan Toll Receipts は独立したサービスで、NEXCO・ETC利用照会サービス（MEISAI）ではありません。',
      keywords: 'ETCカード管理 ソフト,ETC利用明細 自動化,ETC利用照会サービス レポート,法人 ETCカード管理,車両管理 ETC,通行料金レポート,複数車両 ETC 管理,ETC明細 PDF CSV,先行受付 ETC',
    },
    en: {
      title: 'Automate Your Japan ETC Management | Early Access (1-Year Premium Free)',
      desc: 'Centralize ETC cards, vehicles, and toll data. Automatically retrieve ETC usage and deliver daily receipts as PDF + CSV. For individuals, businesses, and government. Reserve an Early Access spot (first 100,000) for a 1-year Premium free trial — email only, no credit card. Independent service; not NEXCO or the ETC Usage Inquiry Service (MEISAI).',
      keywords: 'ETC card management software,ETC multi-vehicle records,ETC usage report automation,Japan corporate ETC card,multi-vehicle ETC records,toll fee report Japan,Japan ETC automation,ETC PDF CSV report',
    },
  },
  '/early-access-personal': {
    ja: {
      title: '個人向けETC利用明細 自動受信｜先行受付（1年間無料）',
      desc: '通勤・出張・確定申告に。日本のETC利用明細・領収書を毎日・毎週メールで自動受信し、PDF・CSVで保管。ETCカード最大10枚対応。先行受付で1年間プレミアム無料トライアル、クレジットカード不要。Japan Toll Receipts は独立したサービスです。',
      keywords: 'ETC利用明細 個人,ETC利用明細 自動,ETC領収書 メール,ETC確定申告,ETC通勤 明細,ETCカード 個人 管理,ETC明細 PDF 保存,先行受付 ETC 個人',
    },
    en: {
      title: 'Personal ETC Toll Receipts, Automatically | Early Access (1-Year Free)',
      desc: 'Get your Japan ETC toll receipts automatically — for commuting, business trips, and tax filing. Daily/weekly email delivery, PDF + CSV you can keep, up to 10 ETC cards. Early Access includes a 1-year Premium free trial, no credit card. Independent service.',
      keywords: 'personal ETC toll receipts Japan,Japan ETC usage statement automated,ETC receipt email Japan,Japan commuter toll receipt,individual ETC report Japan,ETC tax filing Japan',
    },
  },
  '/early-access-business': {
    ja: {
      title: '法人ETCカード・車両・拠点を一元管理｜先行受付（1年間無料）',
      desc: '複数のETCカード・社用車・拠点をまとめて管理。コストを可視化し、PDF・CSVで経費精算とエクスポートを効率化。最大60,000枚規模に対応。先行受付で1年間プレミアム無料トライアル、クレジットカード不要・営業電話なし。法人・官公庁は同一料金。',
      keywords: '法人 ETCカード管理,社用車 ETC 管理,複数車両 ETC 管理,複数ETCカード 管理,ETC経費精算 法人,車両管理 ETC,法人ETC レポート,ETCコーポレートカード 管理,通行料金レポート 法人',
    },
    en: {
      title: 'Manage Fleet ETC Cards, Vehicles & Branches | Early Access (1-Year Free)',
      desc: 'Manage ETC cards, vehicles, branches, and toll reports in one place. See costs clearly and export PDF/CSV for reimbursement. Scales to ~60,000 ETC cards. Early Access includes a 1-year Premium free trial — no credit card, no sales calls.',
      keywords: 'ETC multi-vehicle records,Japan corporate ETC card management,fleet ETC reports Japan,multi-card ETC management,business ETC expense report Japan,multi-vehicle ETC records,Japan toll fee report business',
    },
  },
  '/early-access-government': {
    ja: {
      title: '公用車ETCの可視化・説明責任・確認対応｜先行受付（1年間無料）',
      desc: '部署・指揮系統に沿った権限委譲、レビュー可視化、確認対応のレポート。公用車ETCの説明責任を支援します。目的外利用の兆候確認、読み取り専用の監査担当ビューに対応。先行受付で1年間プレミアム無料トライアル。Japan Toll Receipts は独立したサービスです。',
      keywords: '公用車 ETC 管理,官公庁 ETCカード 管理,公用車 ETC 監査,公用車 ETC 記録,権限委譲 ETC,未承認車両 確認,公共部門 ETC レポート',
    },
    en: {
      title: 'Public-Sector ETC Oversight, Delegated Access & Audit Visibility | Early Access',
      desc: 'Oversight, delegated permissions, reporting, and review visibility for public-sector ETC fleets. Department-structure delegation, possible out-of-policy use review, and read-only auditor views. Early Access includes a 1-year Premium free trial. Independent service.',
      keywords: 'government ETC card management,public sector ETC oversight,government ETC audit Japan,official vehicle ETC records,public-sector ETC accountability Japan,delegated ETC access,government fleet toll reports',
    },
  },
  // Shows exactly what customers receive (email + PDF + CSV + route
  // map). Embeds the live trip-route SVG endpoint as a real preview.
  '/sample-outputs': {
    ja: {
      title: 'お届けする書類のサンプル｜PDF・CSV・経路マップ | Japan Toll Receipts',
      desc: 'Japan Toll Receipts がメールにお届けする3点（PDF利用明細、CSV、経路マップ）の実際のフォーマットをご覧いただけます。確定申告・経費精算で確認資料として使いやすいPDF、Excel/会計ソフトに取り込める CSV、入口IC・出口IC・距離・所要時間が一目でわかる経路マップ。',
      keywords: 'ETC利用明細 PDF サンプル,ETC利用明細 CSV サンプル,Japan Toll Receipts サンプル,ETC領収書 サンプル,ETC利用明細 出力 例,経費精算 ETC レポート 例,ETC利用照会 PDF サンプル',
    },
    en: {
      title: 'Sample Outputs: PDF, CSV, Route Maps | Japan Toll Receipts',
      desc: 'See exactly what arrives in your inbox — a real preview of the PDF usage statement, CSV reimbursement file, and per-trip route map that Japan Toll Receipts emails on your chosen schedule.',
      keywords: 'Japan ETC receipt PDF sample,Japan toll CSV sample,ETC usage statement example,Japan Toll Receipts preview,what does Japan Toll Receipts deliver,sample ETC reimbursement report Japan',
    },
  },

  '/why-automated-not-manual': {
    ja: {
      title: '自動でETC利用明細が届くサービス｜Japan Toll Receipts',
      desc: 'Japan Toll Receiptsは、ETC利用明細・利用証明書・CSVを毎日・毎週・毎月、メールに自動で届けるサービスです。ETC利用照会サービス（MEISAI）の管理画面に毎回ログインする必要も、古いデスクトップソフトを起動する必要もありません。明細取得、PDF発行、CSV出力、経費精算、IC名検索、カード別絞り込み、複数カード対応、日本語・英語両対応 — 必要な書類が、必要なタイミングで、勝手に届きます。',
      keywords: 'ETC利用明細 自動,ETC利用明細 メール 自動配信,ETC利用明細 アプリ,ETC利用照会 自動,ETC利用照会サービス（MEISAI） 自動取得,明細取得 自動,PDF発行 利用証明書 自動,CSV出力 経費精算,IC名 道路名 検索,カード別 金額範囲 絞り込み,複数アカウント ETC,ETC明細 スマホ,高速道路 利用明細 メール,ETCマイレージ 明細,運行管理 ETC,経費管理 ETC,原価管理 ETC,大口 多頻度 割引,ETCコーポレートカード 管理,法人ETCカード 明細,ETC利用明細 ダウンロード',
    },
    en: {
      title: 'Automated Japan ETC Toll Receipts by Email | Japan Toll Receipts',
      desc: 'Japan Toll Receipts emails ETC usage statements, usage certificates, and CSV reimbursement files daily, weekly, or monthly — automatically. No logging into the ETC Usage Inquiry Service (MEISAI) portal, no desktop installer, no manual pulls. Per-trip detail, PDF + CSV exports, IC name search, card-level filtering, multi-card support, full Japanese + English. Made for personal, business, and government users.',
      keywords: 'automated Japan ETC toll receipts,Japan ETC usage statement automated,Japan toll receipt email service,ETC Usage Inquiry Service (MEISAI) automation,Japan ETC corporate card management,ETC multi-vehicle operations management,pre-invoice toll cost visibility,Japan toll PDF CSV automatic,IC name road name search Japan,multi-account ETC bilingual,ETC usage details app alternative',
    },
  },

  // ── Daily Reports / Daily Plus product page ─────────────────────
  '/daily-reports': {
    ja: {
      title: 'Daily Plus｜日次ETC利用明細レポートを毎朝メール配信',
      desc: '毎朝7時(JST)に前日のETC利用明細をメール配信。追跡するETCカードの枚数で課金（1枚目300円＋追加カード250円・最大10枚・税抜）。PDF・CSV・利用証明書添付。経費精算・建設業・運送業に最適。',
      keywords: 'ETC日次レポート,Daily Plus,ETC明細 毎日,ETCカード 10枚,ETC月次レポート,日次ETC利用明細',
    },
    en: {
      title: 'Daily Plus | Daily ETC Toll Reports by Email — Japan Toll Receipts',
      desc: 'Every morning at 7 AM JST: yesterday\'s ETC toll usage in your inbox. Priced per tracked ETC card — ¥300 first card + ¥250 each additional, up to 10 (before tax). PDF + CSV + usage certificate attachments. Built for fleets, contractors, and accounting teams.',
      keywords: 'daily ETC toll report Japan,Daily Plus Japan,daily toll receipt email,Japan ETC daily statement',
    },
  },

  // ── Premium Reports — full feature catalog ──────────────────────
  '/premium-reports': {
    ja: {
      title: 'レポート機能一覧｜日次・週次・月次・個別ETCレポート',
      desc: '日次・週次・月次・個別期間・利用なし日・配信失敗通知の6種類のETC利用明細レポートをご用意。日本語・英語(日英)フォーマット、PDF・CSV・利用証明書添付に対応。',
      keywords: 'ETCレポート 種類,ETC月次レポート,ETC個別レポート,ETC利用明細 PDF,ETC利用明細 CSV,ETC利用証明書',
    },
    en: {
      title: 'Report Features | Daily, Weekly, Monthly & On-Demand ETC Reports',
      desc: 'Six report types: daily, weekly, monthly, on-demand custom-period, no-usage day, and delivery-failure notice. Bilingual JA + EN formatting. PDF, CSV, CSV, and usage certificate attachments included.',
      keywords: 'ETC report types Japan,Japan toll report PDF,Japan toll report CSV,Japan ETC monthly report,custom period ETC report',
    },
  },

  // ── Business Reports / Expense reimbursement ────────────────────
  '/business-reports': {
    ja: {
      title: 'ETC通行料金 経費精算レポート｜法人・建設業・運送業向け',
      desc: 'ETC利用明細による経費精算作業を効率化。会計担当者の手作業を削減し、複数ドライバー・複数車両の統合レポート、利用証明書PDF、CSV出力、確定申告時の確認に役立つ記録整理を支援します。',
      keywords: 'ETC経費精算,ETC法人レポート,ETC領収書 経費,法人ETC利用明細,ETC建設業 経費,ETC運送業 経費,ETC確定申告',
    },
    en: {
      title: 'Business Toll Expense Reports | Reimbursement & Fleet | JTR',
      desc: 'Automate Japan toll expense reporting for fleets, contractors, and accounting teams. Multi-driver / multi-vehicle consolidation, PDF receipts, CSV export, records organized for review. Save hours of manual reconciliation per month.',
      keywords: 'Japan toll expense report,ETC business reimbursement,fleet toll management Japan,corporate ETC report Japan,construction company toll receipts Japan',
    },
  },

  // ── Business landing ────────────────────────────────────────────
  '/business': {
    ja: {
      title: '法人・ビジネス向け ETC利用明細管理サービス｜Japan Toll Receipts',
      desc: '法人・個人事業主・運送業者・建設業者の経費精算を効率化。ETCカード最大10枚を1つのJTR管理アカウントで一括管理、複数ドライバー・社用車対応、PDF・CSV形式の利用記録、日本語・英語出力、車両別・カード別レポート。月数時間の経理作業を削減できる独立運営のサービスです。',
      keywords: 'ETC法人サービス,ETC個人事業主,ETC法人カード レポート,法人ETC利用明細,ETC企業向け,社用車 ETC 管理,法人 ETC 経費精算',
    },
    en: {
      title: 'Business Services | Multi-Card ETC Management for Fleets — Japan Toll Receipts',
      desc: 'Streamline ETC expense management for companies and contractors in Japan. Manage up to 10 ETC cards per account, multi-driver support, bilingual PDF / CSV invoices, and records organized for review.',
      keywords: 'Japan ETC business service,corporate ETC management,fleet ETC reports Japan,ETC card business Japan',
    },
  },

  '/features/authorized-vehicle-monitoring': {
    ja: {
      title: '登録車両との照合 & 未承認車両の可能性レビュー｜Japan Toll Receipts',
      desc: 'ETC利用記録を組織の承認車両リストと照合し、リストにない車両の利用を「要確認」として翌日に表示。判定は人が行います。法人・官公庁・公共部門向け。日次レポートで月末請求を待たずに把握。',
      keywords: '登録車両 照合,承認車両リスト,未承認車両 確認,ETC利用記録 照合,複数車両 ETC レビュー,公用車 ETC,registered vehicle comparison Japan',
    },
    en: {
      title: 'Registered-Vehicle Comparison & Possible Unapproved-Use Review | Japan Toll Receipts',
      desc: 'Compare ETC usage records against your organization\'s approved vehicle registry; activity from a vehicle not on the list is surfaced for human review. Built for business, government, and public-sector fleets in Japan. A flag does not establish unauthorized use.',
      keywords: 'approved vehicle registry,registered vehicle comparison,possible unapproved vehicle use,fleet ETC review Japan,government ETC oversight,public-sector ETC accountability',
    },
  },



  // ── Personal track — Overview / Features / Pricing / Offer ──────
  '/personal': {
    ja: {
      title: '個人ドライバー向けETC利用履歴サービス｜月額300円・1年無料体験',
      desc: '通勤・出張・確定申告・外国人居住者の方の確認用に。日本のETC利用履歴・領収書を毎日・毎週・毎月メールで自動受信。先着100,000件のJTR管理アカウント限定で1年間プレミアム無料体験(クレジットカード登録不要)。ETCカード最大10枚まで対応、日本語・英語の両方で配信。',
      keywords: 'ETC個人ドライバー,ETC利用履歴 個人,ETC利用明細 個人,ETC通勤レポート,ETC確定申告 個人,ETC明細 自動配信,日本 ETCレポート 個人,外国人 ETC 日本,外国人居住者 ETC',
    },
    en: {
      title: 'Personal ETC Toll Receipts | Daily, Weekly & Monthly Email Reports',
      desc: 'Automated Japan ETC toll receipts for individual drivers — commuters, freelancers, expat residents, and tax-filing households. 1-year premium free trial, no credit card. Up to 10 ETC cards. Bilingual JA + EN delivery.',
      keywords: 'personal ETC toll receipts Japan,Japan ETC driver service,individual ETC report Japan,Japan commuter toll statement,Japan tax filing toll receipt',
    },
  },
  '/personal/features': {
    ja: {
      title: '個人プラン 機能一覧｜日次・週次・月次・カスタムETCレポート',
      desc: 'Japan Toll Receiptsの個人プラン全機能。日次・週次・月次・カスタム期間レポート、利用なし日通知、PDF・CSV添付、スマート支出アラート、日本語・英語対応配信、ETCカード最大10枚。',
      keywords: 'ETC個人プラン 機能,ETCレポート 種類,ETC利用明細 カスタム,スマート支出アラート,ETC日本語・英語対応,ETCカード 10枚',
    },
    en: {
      title: 'Personal Plan Features | Daily, Weekly, Monthly & Custom ETC Reports',
      desc: 'Every personal-plan feature in one place: daily / weekly / monthly / custom-range reports, no-usage day confirmations, PDF + CSV attachments, smart spend alerts, bilingual JA + EN, up to 10 ETC cards.',
      keywords: 'Japan ETC personal features,daily ETC report Japan,custom ETC report,smart spend alerts ETC,bilingual ETC report Japan',
    },
  },
  '/personal/pricing': {
    ja: {
      title: '個人プラン 料金｜Free Weekly継続して無料・Daily Plus（1枚目300円＋追加250円）',
      desc: 'Free Weeklyは継続して無料(クレジットカード登録不要)。Daily Plusは追跡するETCカードの枚数で課金（1枚目300円＋追加カード250円・最大10枚・税抜）。先着100,000名の先行受付会員は1年間プレミアム無料体験付き、あとから自動的にFree Weeklyへ移行。年額プランや5年パスはありません。',
      keywords: 'ETC個人 料金,Daily Plus 300円,Free Weekly 無料,ETCカード 枚数 料金,ETC月額 個人,1年間無料体験 ETC',
    },
    en: {
      title: 'Personal Pricing | Free Weekly & Daily Plus per ETC card',
      desc: 'Free Weekly stays free forever. Daily Plus is priced per tracked ETC card — ¥300 first card + ¥250 each additional, up to 10 (before tax). First 100,000 Early Access members get 1 year of Daily Plus at no charge — no credit card required. No annual plan or 5-Year Pass.',
      keywords: 'Japan ETC personal pricing,Daily Plus 300 yen,free ETC report Japan,per ETC card pricing,Japan ETC monthly plan,1-year free ETC trial',
    },
  },
  '/personal/limited-time-offer': {
    ja: {
      title: '【期間限定】個人プラン 1年間プレミアム無料｜先着100,000件のJTR管理アカウント',
      desc: '個人ドライバー向け期間限定キャンペーン。先着100,000件のJTR管理アカウント限定でDaily Plusのプレミアム機能を1年間継続して無料(クレジットカード登録不要)。日次・週次・月次レポート、ETCカード10枚、PDF・CSV・利用証明書を即日ご利用可。',
      keywords: '個人ETC 期間限定,1年間無料 ETC,先着100,000件のJTR管理アカウント,Daily Plus 無料,ETC無料キャンペーン',
    },
    en: {
      title: 'Limited-Time Offer | 1 Year of Premium Free | Japan Toll Receipts Personal',
      desc: 'Limited offer for the first 100,000 personal members: a full year of Daily Plus at no charge. No credit card. Daily, weekly, monthly reports + up to 10 ETC cards + PDF / CSV / certificate attachments.',
      keywords: 'limited time ETC offer Japan,1 year free ETC personal,first 100000 Japan ETC,Daily Plus free year',
    },
  },

  // ── Business track — Features / Pricing / Offer (track-aware) ───
  '/business/features': {
    ja: {
      title: '法人プラン 機能一覧｜16機能・3つの表示モード・4つの活用シナリオ',
      desc: '法人プランの16機能を3つの表示モード（ビジュアル・全機能リスト・活用シナリオ）で解説。日次・週次・月次の自動配信、グループ別宛先振分け、カスタム期間レポート、複数車両可視化、目的外利用の兆候確認、電帳法・インボイス関連の資料整理に役立つ機能を紹介します。',
      keywords: 'ETC法人 機能,車両管理 ETC,法人ETC 連結レポート,グループ宛先振分け,法人ETC 日次,カスタム期間レポート 法人,ETC目的外利用の兆候検知,複数車両 私的利用 検知,法人ETC 活用例',
    },
    en: {
      title: '16 Business Features · Graphics / Full List / Scenarios — JTR',
      desc: '16 multi-vehicle ETC tools across 3 view modes (graphics, full-list, real-world scenarios). Daily / weekly / monthly auto-delivery, group-routed recipients, custom on-demand reports, multi-vehicle dashboard, personal-use review. Includes 4 detailed scenarios for rental vehicles, trucking, construction, and expense review.',
      keywords: 'Japan ETC business features,multi-vehicle ETC management Japan,corporate ETC consolidated report,daily multi-vehicle visibility Japan,ETC expense review Japan,personal-use review Japan,construction vehicle allocation Japan,rental car vehicle records Japan',
    },
  },
  '/business/pricing': {
    ja: {
      title: '法人プラン 料金｜月額1,980円〜（5枚込み）・追跡ETCカード枚数で課金（税抜）',
      desc: '料金は追跡するETCカードの枚数で決まります。月額1,980円で最大5枚込み、6〜500枚目は1枚250円、501枚目以降は1枚200円（最大60,000枚・税抜・消費税10%別途）。背後のMEISAI接続構成はJTRが管理。法人と官公庁は同一料金。毎月自動調整、1年間プレミアム無料体験、クレジットカード登録不要、営業電話なし。',
      keywords: '法人ETC 料金,ETCカード 枚数 料金,1980円 5枚込み,追跡ETCカード 課金,法人ETC 料金 税抜,1年間プレミアム無料,複数車両 ETC 料金,60000枚 ETC',
    },
    en: {
      title: 'Business Pricing | ¥1,980/mo incl. 5 cards, priced per ETC card — Japan Toll Receipts',
      desc: 'Priced per active tracked ETC card. ¥1,980/month includes up to 5 cards; cards 6–500 add ¥250 each; cards above 500 add ¥200 each — up to 60,000 cards (before tax; 10% tax added). JTR manages the underlying MEISAI structure. Business and Government use the same pricing. Auto-adjusts monthly. 1-Year Premium Trial, no credit card, no sales calls.',
      keywords: 'Japan ETC business pricing,fleet ETC pricing Japan,per ETC card pricing,1980 yen 5 cards included,Japan ETC up to 60000 cards,1-Year Premium Trial,Japan ETC organization pricing',
    },
  },
  '/business/limited-time-offer': {
    ja: {
      title: '法人向けETC自動レポート | 先着10,000件のJTR管理アカウント限定',
      desc: '法人ETC利用明細、PDF・CSVレポート、車両別管理をまとめて自動化。JTR法人スイートは先着10,000件のJTR管理アカウント限定です。',
      keywords: '法人ETC 期間限定,1年間プレミアム無料 法人,先着10,000件のJTR管理アカウント,複数車両 ETC 無料',
    },
    en: {
      title: 'Business Limited-Time Offer | 1 Year Premium Free | First 10,000 Accounts',
      desc: 'First 10,000 JTR Management Accounts registered by businesses get one full year of Business Suite premium at no charge — multi-vehicle records, consolidated reports, and smart alerts. No credit card. No sales calls.',
      keywords: 'Japan ETC business offer,1 year free business ETC,first 10000 ETC Usage Inquiry Service (MEISAI) Japan ETC,corporate ETC free trial',
    },
  },
  '/business-suite': {
    ja: {
      title: '法人スイート｜法人ETCカード・多車両管理プラットフォーム｜Japan Toll Receipts',
      desc: 'Japan Toll Receipts 法人スイート — 法人ETCカード・社用車・営業車の利用明細を一元管理する統合プラットフォーム。複数ETC利用照会サービス（MEISAI）アカウントの連携、グループ別連結レポート、車両別・カード別の絞り込み、スマート支出アラート、ボリュームディスカウント対応。',
      keywords: '法人スイート 日本,法人ETCカード 管理,多車両 ETC 管理,社用車 ETC 管理,複数ETC利用照会サービス（MEISAI） 連携,法人ETC 利用明細,法人ETC 一元管理',
    },
    en: {
      title: 'Business Suite | Multi-vehicle ETC Records | Japan Toll Receipts',
      desc: 'Japan Toll Receipts Business Suite — the unified platform for fleets and corporate accounts. Manage multiple ETC Usage Inquiry Service (MEISAI) accounts, group-level consolidated reports, smart spend alerts, and volume-based pricing.',
      keywords: 'Japan ETC Business Suite,multi-vehicle ETC records Japan,corporate ETC reporting,multi-ETC Usage Inquiry Service (MEISAI) management',
    },
  },

  // ── Business — registered vehicle review (needs-review only) ────
  '/business/authorized-vehicles': {
    ja: {
      title: '登録済み車両リスト｜法人ETCカードの目的外利用の兆候を翌日に把握 | JTR',
      desc: 'カードごとに登録済み車両リストを登録し、登録外車両での走行を「要確認」フラグとして翌日に把握。レンタル、運送、建設、リースなど複数車両を扱う法人向け。JTRは独立した民間サービスで、自動的な不正検知や監査の保証は行いません。人によるレビューを前提とした運用です。',
      keywords: '登録車両 リスト 法人,法人ETCカード 監視,ETC目的外利用 兆候,複数車両 私的利用 兆候,要確認フラグ ETC,法人 車両管理 ETC',
    },
    en: {
      title: 'Registered Vehicle Review | Flag Out-of-List ETC Activity | JTR',
      desc: 'Register an registered vehicle list per ETC card and surface activity from non-listed vehicles as next-day needs-review flags. Built for rental, trucking, construction, and lease fleets. JTR is independent and does not provide automated fraud detection or audit guarantees — designed for human review.',
      keywords: 'registered vehicle list business,fleet ETC monitoring Japan,ETC misuse flag Japan,fleet personal use detection,needs-review flag ETC,corporate vehicle ETC management',
    },
  },

  // ── Personal — Family Vehicle List ──────────────────────────────
  '/personal/family-vehicle-list': {
    ja: {
      title: '家族 登録済み車両リスト｜世帯のETC利用を整理 | Japan Toll Receipts',
      desc: '世帯ETCカードに対して登録済み車両リストを登録し、登録外車両での走行を「要確認」フラグとして把握。共有車両のあるご家庭、配偶者・子どもがETCカードを共用するご家庭向け。経費の私的・業務区分の整理にも活用できます。',
      keywords: '家族 登録車両リスト,世帯ETC 管理,共有 ETC カード,家族 ETC利用明細,家庭 ETC 監視,要確認 ETC',
    },
    en: {
      title: 'Family Vehicle List | Organize Household ETC Activity | Japan Toll Receipts',
      desc: 'Register an registered vehicle list for a household ETC card and surface activity from non-listed vehicles as needs-review flags. Built for families with shared cars, spouses and children sharing an ETC card, and households separating personal vs. business trips.',
      keywords: 'family vehicle list Japan,household ETC management,shared ETC card family,family ETC tracking,household toll review Japan',
    },
  },

  // ── Roadmap (public, but noindex per launch policy) ─────────────
  '/roadmap': {
    ja: { title: 'ロードマップ｜JTR ローンチへの道のり', desc: 'Japan Toll Receipts のローンチへ向けたマイルストーン。完了済み、テスト中、進行中、計画中の機能を四半期ごとに公開しています。', keywords: 'Japan Toll Receipts ロードマップ,ETCサービス 進捗,ローンチ マイルストーン' },
    en: { title: 'Roadmap | Japan Toll Receipts Road to Launch', desc: 'Public roadmap for Japan Toll Receipts. Quarter-by-quarter milestones across completed, in-testing, in-progress, and planned categories on the path to launch.', keywords: 'Japan Toll Receipts roadmap,ETC service progress,launch milestones' },
  },

  // ── Phishing Awareness sub-page (security trifold) ──────────────
  '/security/phishing-awareness': {
    ja: {
      title: 'ETCフィッシング詐欺 対策・最新事例｜Japan Toll Receipts',
      desc: '日本のETC利用者を狙う最新のフィッシング詐欺・なりすましメール・偽SMSの実例と対策。本物と偽物の見分け方、公式ドメインホワイトリスト、被害時の対応手順を運営チームが解説。',
      keywords: 'ETCフィッシング 対策,ETC偽メール 最新,ETC詐欺 SMS,ETCなりすまし 警戒,ETC利用照会 偽サイト',
    },
    en: {
      title: 'ETC Phishing Awareness | Latest Scams & Prevention | Japan Toll Receipts',
      desc: 'Stay ahead of fake ETC inquiry emails, SMS phishing, and impersonation scams targeting Japan drivers. Real examples, the official-domain whitelist, and step-by-step recovery if you click a bad link.',
      keywords: 'Japan ETC phishing awareness,fake ETC email examples,Japan ETC SMS scam,Japan toll scam prevention,Japan ETC fraud safety',
    },
  },

  // ── Security & Trust ────────────────────────────────────────────
  '/features': {
    ja: {
      title: '機能一覧｜ETC利用照会サービス（MEISAI）連携・PDF/CSV・複数ETCカード対応 | Japan Toll Receipts',
      desc: 'ETC利用履歴のメール自動配信、ETC利用照会サービス（MEISAI）連携、PDF・CSVダウンロード、ETCカード最大10枚対応、車両別レポート、経費精算サポート、二要素認証、取扱履歴、APPI準拠の取得後すぐに配信する方式によるデータ保護など、Japan Toll Receiptsの主要機能を一覧で紹介。',
      keywords: 'ETC機能,ETC利用履歴 機能,ETC利用照会サービス（MEISAI）連携,ETC PDF CSV,ETCカード10枚,車両別ETCレポート,ETC経費精算,ETC二要素認証,ETC取扱履歴,ETC APPI',
    },
    en: {
      title: 'Features | ETC Usage Inquiry Service Integration, PDF/CSV, Multi-Card — Japan Toll Receipts',
      desc: 'Explore every feature of Japan Toll Receipts: automated ETC Usage Inquiry Service (MEISAI)-connected ETC report delivery, PDF/CSV downloads, up to 10 ETC cards per account, two-factor auth, audit logs, APPI-compliant pass-through data handling, and more.',
      keywords: 'Japan toll receipts features,ETC Usage Inquiry Service (MEISAI) integration features,Japan ETC report features,ETC PDF CSV features',
    },
  },
  '/trust': {
    ja: {
      title: '信頼と安全｜独立運営・運営13年・取得後すぐに配信する方式 — Japan Toll Receipts',
      desc: 'Japan Toll Receiptsの信頼性、セキュリティ姿勢、NEXCO・ETC利用照会サービス（MEISAI）との関係、データ取り扱い、運営体制、フィッシング詐欺からのお守りについてご説明します。当社は2013年から運営の独立サービスです。',
      keywords: 'Japan Toll Receipts 信頼性,ETC独立サービス,NEXCO ETC違い,ETCフィッシング,ETC安全',
    },
    en: {
      title: 'Trust & Safety | Independent Service, Operated by NoJo Enterprise — Japan Toll Receipts',
      desc: "Japan Toll Receipts' trust and safety overview: our security posture, independence from NEXCO/MEISAI, pass-through data handling, operator NoJo Enterprise's operating history since 2013, and phishing safety guidance for ETC customers.",
      keywords: 'Japan toll receipts trust,Japan ETC independent service,Japan ETC phishing safety,Japan toll receipt independent',
    },
  },

  '/security': {
    ja: {
      title: 'セキュリティ・データ保護｜AES-256暗号化・取得後すぐに配信する方式',
      desc: 'AES-256による暗号化、パスワード暗号化、二要素認証、取扱履歴、データマスキング。当社ではETC利用情報を永続保存しない「取得後すぐに配信する方式」を採用。お客様のプライバシーを最優先。',
      keywords: 'ETCセキュリティ,ETCデータ保護,ETC安全性,パススルー データ処理,ETC暗号化',
    },
    en: {
      title: 'Security & Data Protection | AES-256, Pass-Through | JTR',
      desc: 'AES-256 encryption, hashed passwords, audit logs, data masking, and a strict retrieve-and-deliver architecture: live ETC Usage Inquiry Service (MEISAI) toll data is never permanently stored on our servers. Built for compliance-conscious customers.',
      keywords: 'Japan ETC data security,toll receipt privacy Japan,pass-through data architecture,Japan toll receipt encryption',
    },
  },

  // ── FAQ — high-intent informational ─────────────────────────────
  '/faq': {
    ja: {
      title: 'よくある質問｜ETC領収書 再発行・ETC利用照会サービス（MEISAI）設定・料金・経費精算・確定申告',
      desc: 'ETC領収書の再発行、利用明細・利用証明書の違い、ETC利用照会サービス（MEISAI）アカウント設定、料金プラン、1年間プレミアム無料体験、Free Weeklyプラン、レポート配信、法人・社用車対応、経費精算、確定申告、返金ポリシー、データ取り扱いに関するよくあるご質問にお答えします。',
      keywords: 'ETC FAQ,ETC領収書 再発行,ETC利用明細 質問,ETC利用照会サービス（MEISAI） 設定 FAQ,ETC料金 質問,ETC経費精算 質問,ETC領収書 質問,ETC返金,法人ETC 質問,ETC確定申告 FAQ',
    },
    en: {
      title: 'FAQ | ETC Reports, ETC Usage Inquiry Service setup, Pricing & Support — Japan Toll Receipts',
      desc: 'Frequently asked questions about Japan Toll Receipts: ETC Usage Inquiry Service (MEISAI) setup, ETC report types, pricing, the 1-year premium free trial, Free Weekly fallback, pass-through data handling, refunds, and customer support.',
      keywords: 'Japan toll receipts FAQ,Japan ETC FAQ,ETC Usage Inquiry Service (MEISAI) setup FAQ,how to get ETC receipt FAQ,Japan ETC pricing FAQ',
    },
  },

  // ── Contact / Support ────────────────────────────────────────────
  '/contact': {
    ja: {
      title: 'お問い合わせ｜Japan Toll Receipts カスタマーサポート',
      desc: 'Japan Toll Receiptsのカスタマーサポートへお問い合わせください。ETC利用照会サービス（MEISAI）設定、ETCレポート、料金プラン、アカウント、技術的な質問について、平均24時間以内にご返信いたします。',
      keywords: 'Japan Toll Receipts お問い合わせ,ETCサポート,ETC質問 連絡先',
    },
    en: {
      title: 'Contact Support | Japan Toll Receipts',
      desc: 'Get in touch with Japan Toll Receipts customer support. Questions about ETC Usage Inquiry Service (MEISAI) setup, ETC reports, pricing, account access, or technical issues. Average response time: under 24 hours.',
      keywords: 'contact Japan Toll Receipts,ETC support Japan,Japan toll receipt help',
    },
  },

  // ── About / Team / Testimonials — trust signals ─────────────────
  '/about': {
    ja: {
      title: '会社概要｜運営13周年 NoJo Enterprise Co. Ltd. 横須賀',
      desc: 'Japan Toll Receiptsは運営13周年のNoJo Enterprise、Co. Ltd.が運営。横須賀本社。日本全国のETCドライバーのサポート実績。NBC東京2020公式ベンダー。',
      keywords: 'Japan Toll Receipts 会社情報,NoJo Enterprise,横須賀 ETC,運営13周年 ETC',
    },
    en: {
      title: 'About Us | NoJo Enterprise Co. Ltd. — Since 2013 | Japan Toll Receipts',
      desc: 'Japan Toll Receipts is operated by NoJo Enterprise, Co. Ltd., headquartered in Yokosuka. Serving Japan ETC drivers since 2013. Official NBC Tokyo 2020 vendor. Built by drivers, for drivers.',
      keywords: 'about Japan Toll Receipts,NoJo Enterprise Yokosuka,Japan ETC service since 2013',
    },
  },
  '/corporate-profile': {
    ja: {
      title: '会社概要｜運営会社 NoJo Enterprise 株式会社（横須賀・2013年設立）',
      desc: 'Japan Toll Receipts は NoJo Enterprise 株式会社が運営するETC利用レポート・自動配信サービスです。会社名・設立（2013年12月3日）・代表取締役・所在地（神奈川県横須賀市）・事業内容・サービスの関係をご案内します。NEXCO・ETC利用照会サービス（MEISAI）とは別の、独立した記録・レポートサービスです。',
      keywords: 'NoJo Enterprise,NoJo Enterprise 株式会社,Japan Toll Receipts 運営会社,会社概要,ETC レポート 会社,横須賀 ETC 会社,2013年設立,独立サービス ETC',
    },
    en: {
      title: 'Corporate Profile | Operated by NoJo Enterprise Co., Ltd. (Yokosuka, Since 2013)',
      desc: 'Japan Toll Receipts is operated by NoJo Enterprise Co., Ltd., an ETC usage reporting and automation company in Yokosuka, Japan, supporting ETC-related services since 2013. See our company name, founding date, representative, head office, business focus, and the relationship between Japan Toll Receipts and NoJo Enterprise. Independent records and reporting service — not NEXCO or the ETC Usage Inquiry Service (MEISAI).',
      keywords: 'NoJo Enterprise,NoJo Enterprise Co Ltd,Japan Toll Receipts operating company,corporate profile,ETC reporting company Japan,Yokosuka ETC company,established 2013,independent ETC service',
    },
  },
  '/team': {
    ja: { title: '運営チーム｜Japan Toll Receipts', desc: 'Japan Toll Receiptsの運営チームをご紹介します。CEOアダム・ジョーンズを中心に、日本のETC・高速道路関連サービスで10年以上の経験を活かして運営しています。', keywords: 'Japan Toll Receipts チーム,アダム・ジョーンズ,ETCサービス運営' },
    en: { title: 'Our Team | Japan Toll Receipts', desc: 'Meet the team behind Japan Toll Receipts. Led by CEO Adam Jones with over a decade of experience in Japan ETC and highway toll services.', keywords: 'Japan Toll Receipts team,Adam Jones CEO' },
  },
  // Alias for /our-team so SEO meta still updates when visitors land
  // on the legacy URL (it points at the same TeamPage component).
  '/our-team': {
    ja: { title: '運営チーム｜Japan Toll Receipts', desc: 'Japan Toll Receiptsの運営チームをご紹介します。CEOアダム・ジョーンズを中心に、日本のETC・高速道路関連サービスで10年以上の経験を活かして運営しています。', keywords: 'Japan Toll Receipts チーム,アダム・ジョーンズ,ETCサービス運営' },
    en: { title: 'Our Team | Japan Toll Receipts', desc: 'Meet the team behind Japan Toll Receipts. Led by CEO Adam Jones with over a decade of experience in Japan ETC and highway toll services.', keywords: 'Japan Toll Receipts team,Adam Jones CEO' },
  },
  '/testimonials': {
    ja: { title: 'お客様の声｜2,500件以上のレビュー｜Japan Toll Receipts', desc: '運営元NoJo Enterpriseのもと2013年から、建設業、車両記録の担当者、官公庁・公共部門、外国人居住者、個人ドライバーなど幅広いお客様にご利用いただいています。2,500件以上のお客様レビューをご覧ください。', keywords: 'Japan Toll Receipts レビュー,ETCサービス 口コミ,ETC利用明細 評判' },
    en: { title: 'Customer Testimonials | 2,500+ Reviews | Japan Toll Receipts', desc: 'Read 2,500+ customer reviews. Trusted by Japan construction firms, vehicle record owners, public-sector organizations, foreign residents, and individual drivers — through operator NoJo Enterprise since 2013. Official NBC Tokyo 2020 vendor.', keywords: 'Japan Toll Receipts reviews,ETC service testimonials Japan,Japan toll receipt customer feedback' },
  },

  // ── Legal pages ─────────────────────────────────────────────────
  '/terms': {
    ja: { title: '利用規約｜Japan Toll Receipts', desc: 'Japan Toll Receiptsの利用規約です。料金、無料体験、解約、返金、データの取り扱い、サービス継続性、禁止行為について説明しています。', keywords: 'Japan Toll Receipts 利用規約,ETCサービス 利用条件' },
    en: { title: 'Terms of Service | Japan Toll Receipts', desc: 'Japan Toll Receipts Terms of Service. Pricing, trial, cancellation, refund policy, data handling, service continuity, and acceptable use.', keywords: 'Japan Toll Receipts terms,ETC service terms' },
  },
  '/privacy': {
    ja: { title: 'プライバシーポリシー｜個人情報保護｜Japan Toll Receipts', desc: '個人情報の収集、利用、開示、第三者提供、保存期間、お客様の権利について説明します。ETC利用情報は取得後すぐに配信する方式を基本として取り扱います。', keywords: 'Japan Toll Receipts プライバシー,ETC個人情報保護,個人情報保護法' },
    en: { title: 'Privacy Policy | Japan Toll Receipts', desc: 'Privacy policy covering data collection, use, disclosure, third-party sharing, retention, and user rights. GDPR + APPI (Japan Personal Information Protection Act) compliant.', keywords: 'Japan Toll Receipts privacy,ETC privacy Japan,APPI compliant' },
  },
  '/disclaimer': {
    ja: { title: '免責事項｜Japan Toll Receipts', desc: 'Japan Toll Receiptsの免責事項です。当社はNEXCOまたはETC利用照会サービスとは関係がありません。通行料金の請求・徴収は行わず、お客様が許可したMEISAI情報の整理とメール配信を支援します。', keywords: 'Japan Toll Receipts 免責事項,NEXCOではありません,ETC利用照会サービス公式ではありません' },
    en: { title: 'Disclaimer | Japan Toll Receipts', desc: 'Japan Toll Receipts disclaimer. We are NOT NEXCO or an official ETC inquiry service. We do not collect tolls — we help organize and deliver your authorized ETC Usage Inquiry Service (MEISAI) information by email.', keywords: 'Japan Toll Receipts disclaimer,not NEXCO,not official ETC service' },
  },
  '/legal/commercial-transactions': {
    ja: { title: '特定商取引法に基づく表記｜Japan Toll Receipts', desc: '販売業者名、運営責任者、所在地、連絡先、販売料金、支払方法、サービス提供時期、解約、返金ポリシー、重要な注意事項を掲載しています。', keywords: '特定商取引法 表記,Japan Toll Receipts 法的表示,販売業者情報' },
    en: { title: 'Commercial Transactions Disclosure | Japan Toll Receipts', desc: 'Specified Commercial Transactions Act disclosure: vendor name, business address, contact, pricing, payment terms, service delivery, cancellation, refund policy, and disclosures.', keywords: 'commercial transactions disclosure Japan,Japan vendor disclosure' },
  },
  '/legal/refund-policy': {
    ja: { title: '返金ポリシー｜サービス継続性と条件に応じた返金', desc: '月額プランはいつでも解約でき、請求期間の終了日まで利用できます。長期プランは条件に応じて返金またはクレジット対応を行います。解約後もFree Weeklyへ移行できます。', keywords: 'Daily Plus 返金,ETCレポート 返金ポリシー,日割り返金' },
    en: { title: 'Refund Policy | Service Continuity & Fair Pro-Rated Refunds', desc: 'Monthly plans: cancel anytime, billed only for months used. Free Weekly fallback keeps your account live forever. Fair pro-rated credit if the paid service is permanently discontinued.', keywords: 'Japan Toll Receipts refund,Daily Plus refund policy,monthly cancellation' },
  },
  '/data-handling': {
    ja: { title: 'データの取り扱い｜取得後すぐに配信する方式｜Japan Toll Receipts', desc: 'ETC利用照会サービス（MEISAI）から取得したETC利用明細データは、取得・検証・メール配信の後、継続保存しない設計です。サポート目的で配信履歴などの最小限の情報のみを保持します。', keywords: '取得後すぐに配信する方式,ETCデータ取り扱い,ETC利用明細 保存しない' },
    en: { title: 'Data Handling | Pass-Through Architecture | Japan Toll Receipts', desc: 'Live ETC Usage Inquiry Service (MEISAI) toll data is fetched, validated, delivered, and discarded — never permanently stored on our servers. Only your delivery history is retained for support purposes. Privacy by design.', keywords: 'Japan ETC data handling,retrieve-and-deliver architecture,toll data privacy Japan' },
  },
  '/consent-policy': {
    ja: { title: '同意方針｜お客様情報の利用範囲｜Japan Toll Receipts', desc: 'お客様にご同意いただく情報の利用範囲、目的、撤回方法を説明します。マーケティング配信、第三者連携、分析に関する同意は個別に管理できます。', keywords: 'ETC同意方針,個人情報 同意,Japan Toll Receipts 同意管理' },
    en: { title: 'Consent Policy | Japan Toll Receipts', desc: 'Customer consent policy covering data use scope, purpose, and withdrawal. Marketing, third-party sharing, and analytics consents are independently granular and revocable.', keywords: 'Japan Toll Receipts consent,ETC consent policy Japan' },
  },

  // ── Audience-specific landings ──────────────────────────────────
  '/japanese-drivers': {
    ja: {
      title: '日本の個人ドライバー向け ETC利用明細自動配信サービス',
      desc: '日本国内の個人ドライバー・通勤者・出張族・確定申告利用の方へ、ETC利用明細・通行料金レポートを毎日・毎週・毎月メールで自動配信。ETC利用照会サービス（MEISAI）に毎回ログイン不要。PDF・CSVで保管・家計管理・経費精算に活用できます。1年間プレミアム無料体験（クレジットカード登録不要）。',
      keywords: 'ETC日本の個人ドライバー,ETC通勤 利用,ETC確定申告,ETC家計管理,日本国内 ETCレポート,個人 ETC明細 メール',
    },
    en: {
      title: 'For Japanese Drivers | Automated ETC Reports — Japan Toll Receipts',
      desc: 'Automated ETC toll receipts for drivers in Japan. Commuters, tax-filing households, expense-tracking professionals — get organized weekly receipts in your inbox.',
      keywords: 'Japan ETC drivers,Japanese commuter toll,Japan tax filing toll receipt',
    },
  },
  '/foreign-residents': {
    ja: {
      title: '外国人居住者向け 英語対応ETCレポート',
      desc: '日本在住の外国人ドライバー・SOFA関係の個人居住者（私有車両）・契約業者・駐在員の方向け、日本語・英語の両方で利用明細を確認できるバイリンガル対応のETCレポート配信。PDF利用証明書・CSV・経路マップを毎日・毎週・毎月メールで自動受信。Japanese ETC receipts in English supported.',
      keywords: '外国人 ETC日本,英語 ETC明細,駐在員 ETC,SOFA 個人 ETC,Japan ETC English,foreign resident ETC Japan',
    },
    en: {
      title: 'ETC Toll Receipts for Foreign Residents in Japan | Bilingual EN/JA',
      desc: 'Japanese ETC toll receipts in English. Built for foreign residents living in Japan — including SOFA-related residents driving personal (privately owned) vehicles, contractors, and expat professionals. Bilingual JA + EN reports. Personal-use service, not for official fleet vehicles.',
      keywords: 'Japan ETC foreign residents,Japan ETC English,SOFA personal ETC Japan,expat ETC Japan,Japan toll receipts in English',
    },
  },

  // ── Onboarding / Setup ──────────────────────────────────────────
  '/legal-etc-setup': {
    ja: {
      title: 'ETC利用照会サービスの登録・使い方｜事前に揃える5項目と注意点',
      desc: 'ETC利用照会サービス（MEISAI）への新規登録に必要な5項目（ETCカード番号・メール・利用年月日・車両番号・19桁の車載器管理番号）を整理。約4時間の反映遅延、450日無利用での自動解約、過去15か月の閲覧範囲など、運用前に押さえておきたい注意点も2026年版で解説。',
      keywords: 'ETC利用照会サービス 使い方,ETC利用照会サービス 登録,ETC利用照会サービス 登録方法,MEISAI 設定,ETCカード番号 確認,ETC車載器管理番号,19桁 車載器管理番号,ETC利用照会サービス 反映時間,ETC利用照会サービス 450日',
    },
    en: {
      title: 'ETC Usage Inquiry Service setup Guide | Required ETC Card Information — Japan Toll Receipts',
      desc: 'Step-by-step guide to ETC Usage Inquiry Service (MEISAI) (ETC Inquiry Service) registration: 5 required items (card number, email, recent drive date, license plate, 19-digit OBU number), plus ~4-hour reflection latency, 450-day inactivity cutoff, and the 15-month data window.',
      keywords: 'ETC Usage Inquiry Service (MEISAI) setup guide,Japan ETC card registration,ETC OBU number Japan,ETC card information lookup,ETC inquiry service how to,ETC inquiry service registration,MEISAI 19-digit OBU,Japan ETC 450 days',
    },
  },

  // ── Interactive Tools — Toll Trip Status Checker ──────────────
  '/toll-trip-status-checker': {
    ja: {
      title: 'ETC利用明細は反映されましたか？走行日から即時判定する無料ツール',
      desc: 'ETC利用照会サービス（MEISAI）への反映タイミングを、走行日を入力するだけで即時判定する無料ツール。約4時間の反映遅延、個人ETCカード15か月／ETCコーポレートカード62日の閲覧範囲も考慮。「ETC利用明細 反映されない」「ETC領収書 出ない」「ETC利用照会サービス 遅い」とお悩みの方はこのページで原因がすぐにわかります。',
      keywords: 'ETC利用明細 反映,ETC利用明細 反映されない,ETC利用明細 いつ反映,ETC利用照会サービス 反映 遅い,ETC領収書 反映,ETC明細 反映 時間,ETC利用照会サービス 時間,ETC利用明細 更新時間,ETC利用照会 4時間,ETC利用明細 15か月,ETCコーポレートカード 62日,ETC明細 反映 確認,ETC領収書 出ない,アクアライン ETC 反映,ETC領収書 レンタカー 反映',
    },
    en: {
      title: 'Did Your ETC Toll Trip Post Yet? · Free Instant Checker — Japan Toll Receipts',
      desc: 'Free instant tool to check whether the ETC Usage Inquiry Service (MEISAI) should already show your ETC toll trip. Accounts for the ~4-hour reflection delay and the 15-month (personal cards) / 62-day (corporate cards) MEISAI windows. Solve "ETC trip not showing", "ETC receipt missing", and "MEISAI delayed" questions instantly.',
      keywords: 'Japan ETC trip not posting,Japan ETC toll not showing MEISAI,Japan ETC receipt missing,ETC Usage Inquiry Service delayed,Japan ETC reflection time,Japan MEISAI 4 hours,Japan ETC 15 months window,Japan ETC corporate card 62 days,Japan ETC trip checker,Japan ETC posting time',
    },
  },

  // ── Guides hub + topical guides ─────────────────────────────────
  '/guides': {
    ja: {
      title: 'ETCガイド一覧｜ETC利用明細・ETC領収書・ETC利用照会サービス・ETCマイレージ・高速道路料金まで135本以上のガイド',
      desc: 'ETC利用明細・ETC利用履歴・ETC領収書・ETC利用証明書の確認方法、ETC利用照会サービス（MEISAI）の登録・使い方・ログイン、ETCマイレージサービス、高速道路料金・高速料金検索、ETC割引、ETC車載器管理番号、ETC再セットアップ・ETCセットアップ証明書、法人ETCカード・ETCコーポレートカード、社用車ETC・営業車ETC、レンタカーETC・カーシェアETC、ETC経費精算・ETC仕訳・ETCインボイスまで、135本以上のETC関連ガイドを公開中。',
      keywords: 'ETCガイド,ETC利用明細 ガイド,ETC領収書 ガイド,ETC利用証明書 ガイド,ETC利用照会サービス 使い方,ETC利用照会サービス 登録,ETC利用照会サービス ログイン,ETCマイレージサービス ガイド,ETCマイレージ ログイン,ETCマイレージ 登録,高速道路料金 確認,高速料金検索,高速料金計算,ETC割引 確認,ETC休日割引,ETC深夜割引,ETC車載器管理番号 確認,ETC再セットアップ,ETCセットアップ証明書,法人ETCカード 管理,ETCコーポレートカード,社用車ETC 管理,営業車ETC,レンタカーETC 領収書,カーシェアETC,タイムズカーETC,ETC経費精算 ガイド,ETC仕訳,ETC勘定科目,ETCインボイス',
    },
    en: {
      title: 'Japan ETC Guides | 135+ Independent Guides on Receipts & the ETC Usage Inquiry Service',
      desc: '135+ independent guides covering Japan ETC toll receipts, ETC usage statements (ETC利用明細), ETC usage certificates (ETC利用証明書), ETC Usage Inquiry Service (MEISAI) registration / login / troubleshooting, ETC Mileage Service, Japan highway toll fee search and calculation, ETC discounts (weekend / night / ETC 2.0), OBU management number, ETC setup certificate, corporate ETC cards, fleet/company-car management, rental-car and carshare ETC, expense reports, qualified-invoice (インボイス制度) compliance, and more.',
      keywords: 'Japan ETC guides,Japan toll receipt guide,Japan ETC usage statement guide,Japan ETC usage certificate guide,Japan MEISAI registration guide,Japan MEISAI login,Japan ETC Mileage Service guide,Japan highway toll fee search,Japan toll fee calculator,Japan ETC discount guide,Japan ETC weekend discount,Japan ETC night discount,Japan ETC OBU management number,Japan ETC setup certificate,Japan corporate ETC card guide,Japan fleet ETC,Japan rental car ETC,Japan carshare ETC,Japan ETC expense report guide,Japan qualified invoice ETC',
    },
  },
  '/etc-ryoshusho-riyoumeisai-guide': {
    ja: {
      title: 'ETC領収書・利用明細・利用証明書の違いと発行方法【4つのルート】',
      desc: 'ETC領収書はその場では出ません。①ETC利用照会サービスの利用証明書（PDF・適格簡易請求書）、②SA/PAの利用履歴発行プリンター、③カード会社の利用明細、④一般レーンで現金・カード払い、の4つのルートを実務向けに比較。NEXCO・首都高速・阪神高速・本四高速の通行記録もまとめて確認。',
      keywords: 'ETC領収書,ETC利用明細,ETC利用証明書,ETC領収書 発行,ETC領収書 違い,ETC利用明細 確認方法,ETC利用履歴 印刷,ETC領収書 PDF,ETC利用証明書 発行,ETC領収書 経費,ETC領収書 インボイス,ETC インボイス 利用証明書,NEXCO 領収書,NEXCO 利用明細,首都高速 ETC 明細,阪神高速 ETC 明細',
    },
    en: { title: 'ETC Receipt vs Usage Statement vs Certificate — Japan Guide', desc: 'Understand the differences between ETC receipts, usage statements (riyou meisai), and usage certificates (riyou shoumeisho) in Japan. When to use each, how to get them, and how to file taxes.', keywords: 'ETC receipt vs usage statement Japan,Japan ETC usage certificate,Japan toll documentation guide' },
  },
  '/japan-etc-receipt-guide': {
    ja: { title: 'Japan ETC利用証明書ガイド｜JapanTollReceipts.jp', desc: '英語圏向けに作成されたJapan ETC領収書の入手・整理ガイド(英語ページ)。', keywords: 'Japan ETC receipt guide' },
    en: {
      title: 'How to Get ETC Toll Receipts in Japan — Complete 2026 Guide',
      desc: 'Complete English-language guide to ETC toll receipts in Japan: what they are, where ETC Usage Inquiry Service (MEISAI) fits, how to download PDF + CSV, how foreign residents can navigate the system, and how to use receipts for business expense reports.',
      keywords: 'how to get ETC receipt Japan,Japan ETC receipt guide,Japan toll receipt English,ETC Usage Inquiry Service (MEISAI) receipt download Japan',
    },
  },
  '/etc-keihi-seisan-report': {
    ja: {
      title: 'ETC通行料金の経費精算【仕訳例・旅費交通費・確定申告まで】',
      desc: 'ETC高速代の経費精算を実務目線で整理。旅費交通費・車両費の使い分け、ETC後払いの仕訳例（未払金→普通預金）、利用証明書・カード明細の使い分け、個人事業主の確定申告、freee/マネーフォワードへのCSV取込まで2026年版で解説。',
      keywords: 'ETC経費精算,ETC領収書 経費,ETC法人カード 経費,ETC確定申告,ETC個人事業主 経費,ETC 仕訳,旅費交通費 ETC,高速代 経費精算,ETC freee,ETC マネーフォワード',
    },
    en: { title: 'ETC Toll Expense Reports for Business — Japan Guide', desc: 'How to handle ETC toll expense reimbursement in Japan: corporate vs personal cards, receipt requirements, tax filing, accounting software integration.', keywords: 'Japan ETC expense report,ETC reimbursement Japan,Japan toll expense for business' },
  },
  '/japan-toll-expense-reports': {
    ja: { title: 'Japan Toll Expense Reports｜法人ETC経費精算(英語ページ)', desc: '法人ETC経費精算ガイドの英語版ページ。', keywords: 'Japan toll expense report' },
    en: {
      title: 'Japan Toll Expense Reports for Business | Reimbursement Guide',
      desc: 'A complete guide for organizing Japan ETC toll expenses for reimbursement, payroll deduction, and tax filing. Covers multi-vehicle records, contractor billing, and accounting software import (Yayoi, freee, Money Forward).',
      keywords: 'Japan toll expense report,Japan ETC reimbursement guide,Japan corporate toll receipts,toll receipts for travel claim Japan',
    },
  },
  '/etc-pdf-csv-download-guide': {
    ja: {
      title: 'ETC利用明細をPDF・CSVでダウンロードする方法【freee・MF対応】',
      desc: 'ETC利用明細をPDF・CSVで取得・保存する方法を、ETC利用照会サービス・JapanTollReceipts・SA/PAプリンターの3ルートで解説。freee／マネーフォワード／弥生／勘定奉行クラウドへのCSV取込列マッピングと、15か月制限を超えてデータを保管する方法まで実務向けに整理。',
      keywords: 'ETC PDF ダウンロード,ETC CSV ダウンロード,ETC明細 PDF 保存,ETC利用明細 CSV,ETC明細 印刷,ETC CSV freee,ETC CSV マネーフォワード,ETC 明細 取込,ETC 会計ソフト',
    },
    en: { title: 'How to Download ETC Reports as PDF / CSV — Japan Guide', desc: 'Step-by-step: how to get ETC toll data as PDF and CSV in Japan. Covers ETC Usage Inquiry Service (MEISAI) download, accounting-software import, and the highway service-area printer kiosks.', keywords: 'Japan ETC PDF download,Japan ETC CSV export,Japan toll data download' },
  },
  // Iter 264 (2026-05-24) — freee / Money Forward Cloud CSV import guide.
  // Strictly NEUTRAL wording: no partnership, no certification, no
  // official-integration claims. JTR is an independent organising service.
  '/guides/etc-csv-freee-moneyforward-import-guide': {
    ja: {
      title: 'ETC CSVをfreee・マネーフォワードに整理する手順｜中立独立ガイド',
      desc: 'ETC利用照会サービスやJTRの日次配信CSVを、freeeやマネーフォワード クラウド会計の公開インポート画面に取り込みやすい形へ整える独立ガイド。列マッピング、自動仕訳ルール、電帳法のファイル命名、インボイス制度の保存要件、消費税区分の前提までまとめました。最終判断は各会計ソフトの公式ヘルプおよび顧問税理士にご確認ください。',
      keywords: 'ETC CSV freee,ETC CSV マネーフォワード,ETC CSV 取込,ETC 自動仕訳,ETC 旅費交通費,ETC 電帳法 ファイル名,ETC インボイス 保存',
    },
    en: {
      title: 'ETC CSV → freee / Money Forward Cloud Import Guide (Independent)',
      desc: 'How to organise ETC usage CSV (from ETC Usage Inquiry Service (MEISAI) or from JTR\'s daily delivery) for clean import via the publicly documented flows in freee and Money Forward Cloud. Covers column mapping, auto-journal rules, Electronic Bookkeeping Law file naming, qualified-invoice retention, and consumption-tax classification. Final decisions belong to your tax advisor.',
      keywords: 'Japan ETC CSV import,freee CSV import Japan,Money Forward Cloud CSV import,ETC accounting software Japan',
    },
  },
  // Iter 267 (2026-05-24) — Carshare & rental-car ETC receipts guide.
  // STRICTLY NEUTRAL: no Times Car / Anyca / d Carshare / Careco / Earthcar
  // partnership, certification, or official-support claim. Independent guide.
  '/guides/etc-carshare-ryoshusho-guide': {
    ja: {
      title: 'カーシェア・レンタカーのETC利用料と領収書の整理ガイド｜中立独立',
      desc: 'カーシェア（Times Car・Anyca・dカーシェア・カレコ・Earthcar）やレンタカーのETC通行料の請求の仕組みと領収書の取り扱いを中立に整理した独立ガイド。事業者ETC利用と持ち込みETCカード利用の違い、JTRが整理できる範囲・できない範囲、インボイス制度・経費精算の証憑の実務まで、各社の公開情報をもとに整理しました。最終判断は各事業者のヘルプおよび顧問税理士にご確認ください。',
      keywords: 'カーシェア ETC 領収書,Times Car ETC 領収書,Anyca ETC,dカーシェア ETC,レンタカー ETC 明細,カーシェア インボイス,カーシェア 経費精算',
    },
    en: {
      title: 'Carshare & Rental-Car ETC Receipts — Independent Neutral Guide',
      desc: 'How carshare operators (Times Car, Anyca, d Carshare, Careco, Earthcar) and rental-car operators bill ETC tolls in Japan, what receipts they issue, and what Japan Toll Receipts (an independent organising service) can and cannot retrieve. Covers operator-card vs bring-your-own-card flows, qualified-invoice treatment, and reimbursement evidence.',
      keywords: 'Japan carshare ETC receipt,Times Car ETC receipt,Japan rental car ETC toll,bring your own ETC card Japan',
    },
  },
  '/guides/etc-carshare-receipt-guide': {
    ja: {
      title: 'カーシェア・レンタカーのETC利用料と領収書の整理ガイド｜中立独立',
      desc: 'カーシェアやレンタカーのETC通行料・領収書の取り扱いを中立に整理した独立ガイド。各社の公開情報をもとに整理しています。',
      keywords: 'Japan carshare ETC,Japan rental car ETC,Times Car ETC',
    },
    en: {
      title: 'Carshare & Rental-Car ETC Receipts — Independent Neutral Guide',
      desc: 'How carshare and rental-car operators bill ETC tolls in Japan, what receipts they issue, and what JTR can/cannot retrieve. Independent and neutral.',
      keywords: 'Japan carshare ETC receipt,Japan rental car ETC toll,bring your own ETC card Japan',
    },
  },
  // Iter 268 (2026-05-24) — Rental-car operator ETC receipt guide.
  // STRICTLY NEUTRAL: no Orix / Nippon / Toyota / Times Car / Nissan
  // partnership, certification, or official-support claim.
  '/guides/rental-car-company-etc-receipt-guide': {
    ja: {
      title: 'レンタカー会社別 ETC領収書・利用明細の確認方法｜中立独立ガイド',
      desc: 'オリックスレンタカー・ニッポンレンタカー・トヨタレンタカー・タイムズカー・日産レンタカーのETC通行料の取り扱いと領収書発行ルートを中立に整理した独立ガイド。3つの利用パターン(レンタカー会社カード / 持ち込みETCカード / 一時貸出)の違い、インボイス制度・適格簡易請求書の取り扱い、ETC割引適用条件、JTRが整理できる範囲までまとめました。最終判断は各社のヘルプおよび顧問税理士にご確認ください。',
      keywords: 'レンタカー ETC 領収書,レンタカー ETC 明細,オリックスレンタカー ETC,ニッポンレンタカー ETC,トヨタレンタカー ETC,タイムズカー レンタル ETC,日産レンタカー ETC,レンタカー インボイス',
    },
    en: {
      title: 'ETC Receipts by Rental-Car Operator (Japan) — Independent Neutral Guide',
      desc: 'How Orix Rent-a-Car, Nippon Rent-a-Car, Toyota Rent-a-Car, Times Car Rental, and Nissan Rent-a-Car bill ETC tolls and issue receipts. Covers the 3 billing patterns (operator card / BYO card / temporary card rental), qualified-invoice retention, ETC discount eligibility, and what JTR can / cannot retrieve.',
      keywords: 'Japan rental car ETC receipt,Orix rent a car ETC,Nippon rent a car ETC,Toyota rent a car ETC,Times Car Rental ETC,Japan rental car qualified invoice',
    },
  },
  '/etc-pdf-csv-report-guide': {
    ja: { title: 'PDF・CSVレポートガイド (英語版)｜JapanTollReceipts.jp', desc: 'ETC利用明細をPDF・CSVで管理する方法の英語版ガイドページ。', keywords: 'ETC PDF CSV guide' },
    en: {
      title: 'ETC PDF & CSV Report Guide for Japan | Download, Export, Import',
      desc: 'How to download Japan ETC toll data as PDF + CSV, import into Excel, Google Sheets, Yayoi, freee, Money Forward, and QuickBooks. Includes file-format specs and field-mapping guidance.',
      keywords: 'Japan ETC PDF CSV,Japan toll PDF download,Japan toll CSV export,ETC accounting import',
    },
  },
  // Sprint 2 (2026-05-23) — Japanese ETC利用明細 pillar page
  '/guides/etc-riyou-meisai-kanzen-guide': {
    ja: {
      title: 'ETC利用明細・利用履歴の確認方法｜PDF・CSV保存と自動メール配信',
      desc: 'ETC利用明細・利用履歴・利用証明書・領収書の違いと確認方法を解説。PDF/CSV保存、経費精算、法人ETC管理、Japan Toll Receiptsによる自動メール配信まで、2026年版の完全ガイド。',
      keywords: 'ETC利用明細,ETC利用履歴,高速道路 利用明細,ETC 利用明細 確認,ETC 利用履歴 確認,ETC 利用証明書,ETC 領収書,ETC 明細 PDF,ETC 明細 CSV,ETC 経費精算',
    },
    en: {
      title: 'ETC Usage Statement & History Complete Guide (Japan, 2026)',
      desc: 'How to confirm Japanese ETC usage statements, history, certificates, and receipts; save as PDF or CSV; reconcile expenses; and automate delivery by email — complete 2026 guide.',
      keywords: 'Japan ETC usage statement,Japan ETC history,Japan toll usage statement,Japan ETC certificate,Japan ETC PDF CSV',
    },
  },
  '/guides/etc-usage-statement-complete-guide': {
    ja: {
      title: 'ETC利用明細・利用履歴の確認方法｜PDF・CSV保存と自動メール配信',
      desc: 'ETC利用明細・利用履歴・利用証明書・領収書の違いと確認方法を解説。PDF/CSV保存、経費精算、法人ETC管理、Japan Toll Receiptsによる自動メール配信まで、2026年版の完全ガイド。',
      keywords: 'ETC利用明細,ETC利用履歴,高速道路 利用明細,ETC 利用明細 確認,ETC 利用履歴 確認,ETC 利用証明書,ETC 領収書,ETC 明細 PDF,ETC 明細 CSV,ETC 経費精算',
    },
    en: {
      title: 'ETC Usage Statement & History Complete Guide (Japan, 2026)',
      desc: 'How to confirm Japanese ETC usage statements, history, certificates, and receipts; save as PDF or CSV; reconcile expenses; and automate delivery by email — complete 2026 guide.',
      keywords: 'Japan ETC usage statement,Japan ETC history,Japan toll usage statement,Japan ETC certificate,Japan ETC PDF CSV',
    },
  },
  // Sprint 3 (2026-05-23) — Japanese ETC明細 反映遅延 pillar
  '/guides/etc-meisai-han-ei-osoi-riyu': {
    ja: {
      title: 'ETC利用明細が反映されない・遅い理由｜更新タイミングと実務での対処',
      desc: 'ETC利用明細・利用履歴がいつ反映されるか、なぜ遅れるか、経費精算・確定申告にどう影響するか、Japan Toll Receiptsで何が補助できるかを、自然な日本語で実務向けに整理した2026年版ガイド。',
      keywords: 'ETC利用明細 反映されない,ETC利用履歴 反映 遅い,ETC 明細 更新時間,ETC利用照会サービス 遅い,高速道路 利用履歴 いつ反映,ETC 明細 いつ見れる,ETC利用照会サービス 更新タイミング',
    },
    en: {
      title: 'Why ETC Usage Statements Are Delayed — Timing, Reasons, What to Do',
      desc: 'Why Japan ETC and ETC Usage Inquiry Service (MEISAI) data is not real-time, realistic timing expectations, business impact, and what Japan Toll Receipts can — and cannot — help with.',
      keywords: 'Japan ETC statement delay,Japan ETC update timing,ETC Usage Inquiry Service (MEISAI) reflection delay,Japan toll history when reflected',
    },
  },
  '/guides/etc-statement-delay-guide': {
    ja: {
      title: 'ETC利用明細が反映されない・遅い理由｜更新タイミングと実務での対処',
      desc: 'ETC利用明細・利用履歴がいつ反映されるか、なぜ遅れるか、経費精算・確定申告にどう影響するか、Japan Toll Receiptsで何が補助できるかを、自然な日本語で実務向けに整理した2026年版ガイド。',
      keywords: 'ETC利用明細 反映されない,ETC利用履歴 反映 遅い,ETC 明細 更新時間,ETC利用照会サービス 遅い,高速道路 利用履歴 いつ反映,ETC 明細 いつ見れる,ETC利用照会サービス 更新タイミング',
    },
    en: {
      title: 'Why ETC Usage Statements Are Delayed — Timing, Reasons, What to Do',
      desc: 'Why Japan ETC and ETC Usage Inquiry Service (MEISAI) data is not real-time, realistic timing expectations, business impact, and what Japan Toll Receipts can — and cannot — help with.',
      keywords: 'Japan ETC statement delay,Japan ETC update timing,ETC Usage Inquiry Service (MEISAI) reflection delay,Japan toll history when reflected',
    },
  },
  // Sprint 4 (2026-05-23) — ETC利用照会サービス vs JTR comparison pillar
  '/comparison/etc-riyou-shoukai-vs-jtr': {
    ja: {
      title: 'ETC利用照会サービスとJapan Toll Receiptsの違い｜独立した補助サービスとしての位置づけ',
      desc: '公的に運営されるETC利用照会サービスと、独立運営の整理・配信サービスであるJapan Toll Receiptsの違いを中立的に比較。両者の役割・運用面・反映タイミング・法人運用・経費精算ワークフローでの違いと、併用のメリットを整理。',
      keywords: 'ETC利用照会サービス 違い,ETC利用照会サービス 比較,ETC利用履歴 管理,ETC 明細 自動化,法人 ETC 管理,ETC CSV PDF,ETC 経費精算,ETC利用照会サービス 法人',
    },
    en: {
      title: 'ETC利用照会サービス vs Japan Toll Receipts — A Neutral Comparison',
      desc: 'Neutral comparison between the official ETC利用照会サービス and Japan Toll Receipts, an independent organisational and reporting layer.',
      keywords: 'ETC inquiry service vs JTR,Japan ETC inquiry comparison,Japan ETC reporting layer,independent ETC organiser',
    },
  },
  '/comparison/etc-inquiry-service-vs-jtr': {
    ja: {
      title: 'ETC利用照会サービスとJapan Toll Receiptsの違い｜独立した補助サービスとしての位置づけ',
      desc: '公的に運営されるETC利用照会サービスと、独立運営の整理・配信サービスであるJapan Toll Receiptsの違いを中立的に比較。両者の役割・運用面・反映タイミング・法人運用・経費精算ワークフローでの違いと、併用のメリットを整理。',
      keywords: 'ETC利用照会サービス 違い,ETC利用照会サービス 比較,ETC利用履歴 管理,ETC 明細 自動化,法人 ETC 管理,ETC CSV PDF,ETC 経費精算,ETC利用照会サービス 法人',
    },
    en: {
      title: 'ETC利用照会サービス vs Japan Toll Receipts — A Neutral Comparison',
      desc: 'Neutral comparison between the official ETC利用照会サービス and Japan Toll Receipts, an independent organisational and reporting layer.',
      keywords: 'ETC inquiry service vs JTR,Japan ETC inquiry comparison,Japan ETC reporting layer,independent ETC organiser',
    },
  },
  // Sprint 5 (2026-05-23) — 法人ETC明細管理 commercial pillar
  '/guides/houjin-etc-meisai-kanri': {
    ja: {
      title: '法人ETCカードの明細管理｜車両別レポートと経費精算をわかりやすく',
      desc: '複数の法人ETCカード・ETCコーポレートカード・社用車の利用履歴を、車両別・カード別に整理する方法を解説。過去62日制約への対応、PDF・CSVレポート、出張・営業車の経費精算、3点突合での社内コンプライアンス確認まで、独立運営のJTRが手助けできる範囲を率直にまとめています。',
      keywords: '法人ETCカード 明細,法人ETC 管理,ETCカード 法人 利用履歴,社用車 ETC 管理,営業車 ETC 管理,高速代 経費精算,車両別 ETC 利用履歴,車両別 ETC 明細,複数 ETCカード 管理,ETC 明細 CSV,業務用 ETCカード 管理,部門別 ETC 利用管理,ETC コーポレートカード 明細,ETC コーポレートカード 利用履歴,ETCカード別 明細,ETC 不正利用 確認,ETC 私的利用 確認,ETC レポート 自動化,ETC 出張 精算',
    },
    en: {
      title: 'Corporate ETC Statement Management — Vehicle Reports & Reimbursement',
      desc: 'How to organise multiple corporate ETC cards and company vehicles by vehicle and card, deliver PDF/CSV reports, and integrate with reimbursement workflows.',
      keywords: 'corporate ETC Japan,Japan fleet ETC management,Japan ETC multi-card,Japan ETC vehicle-by-vehicle report',
    },
  },
  '/guides/corporate-etc-statement-management': {
    ja: {
      title: '法人ETCカードの明細管理｜車両別レポートと経費精算をわかりやすく',
      desc: '複数の法人ETCカード・ETCコーポレートカード・社用車の利用履歴を、車両別・カード別に整理する方法を解説。過去62日制約への対応、PDF・CSVレポート、出張・営業車の経費精算、3点突合での社内コンプライアンス確認まで、独立運営のJTRが手助けできる範囲を率直にまとめています。',
      keywords: '法人ETCカード 明細,法人ETC 管理,ETCカード 法人 利用履歴,社用車 ETC 管理,営業車 ETC 管理,高速代 経費精算,車両別 ETC 利用履歴,車両別 ETC 明細,複数 ETCカード 管理,ETC 明細 CSV,業務用 ETCカード 管理,部門別 ETC 利用管理,ETC コーポレートカード 明細,ETC コーポレートカード 利用履歴,ETCカード別 明細,ETC 不正利用 確認,ETC 私的利用 確認,ETC レポート 自動化,ETC 出張 精算',
    },
    en: {
      title: 'Corporate ETC Statement Management — Vehicle Reports & Reimbursement',
      desc: 'How to organise multiple corporate ETC cards and company vehicles by vehicle and card, deliver PDF/CSV reports, and integrate with reimbursement workflows.',
      keywords: 'corporate ETC Japan,Japan fleet ETC management,Japan ETC multi-card,Japan ETC vehicle-by-vehicle report',
    },
  },
  // Iter 281 (2026-05-24) — ETC利用証明書とETC領収書の違い dedicated guide
  '/guides/etc-riyou-shoumeisho-ryoshusho-chigai-guide': {
    ja: {
      title: 'ETC利用証明書とETC領収書の違い｜独立解説',
      desc: 'ETC利用証明書（高速道路利用証明書）とETC領収書の違い、各書類の発行元・記載項目・適格請求書としての扱いを、Japan Toll Receiptsが中立に整理した独立解説です。',
      keywords: 'ETC利用証明書,ETC利用証明書 発行,ETC利用証明書 ダウンロード,ETC利用証明書 PDF,ETC利用証明書 印刷,ETC利用証明書 インボイス,ETC領収書 利用証明書 違い,高速道路利用証明書,高速道路利用証明書 発行',
    },
    en: {
      title: 'ETC Usage Certificate vs ETC Receipt — Independent Reference',
      desc: 'Independent neutral reference comparing the ETC usage certificate (高速道路利用証明書), the ETC receipt, and the ETC usage statement — issuers, fields, qualified-invoice handling, and how Japan Toll Receipts organises records.',
      keywords: 'Japan ETC usage certificate,Japan ETC receipt difference,ETC riyou shoumeisho,ETC ryoshusho,Japan qualified invoice ETC',
    },
  },
  '/guides/etc-usage-certificate-vs-receipt-difference-guide': {
    ja: {
      title: 'ETC利用証明書とETC領収書の違い｜独立解説',
      desc: 'ETC利用証明書（高速道路利用証明書）とETC領収書の違い、各書類の発行元・記載項目・適格請求書としての扱いを、Japan Toll Receiptsが中立に整理した独立解説です。',
      keywords: 'ETC利用証明書,ETC利用証明書 発行,ETC利用証明書 ダウンロード,ETC利用証明書 PDF,ETC利用証明書 印刷,ETC利用証明書 インボイス,ETC領収書 利用証明書 違い,高速道路利用証明書,高速道路利用証明書 発行',
    },
    en: {
      title: 'ETC Usage Certificate vs ETC Receipt — Independent Reference',
      desc: 'Independent neutral reference comparing the ETC usage certificate (高速道路利用証明書), the ETC receipt, and the ETC usage statement — issuers, fields, qualified-invoice handling, and how Japan Toll Receipts organises records.',
      keywords: 'Japan ETC usage certificate,Japan ETC receipt difference,ETC riyou shoumeisho,ETC ryoshusho,Japan qualified invoice ETC',
    },
  },
  // Iter 282 (2026-05-24) — ETC再セットアップ / セットアップ証明書 workflow guide
  '/guides/etc-resetup-setup-certificate-guide': {
    ja: {
      title: 'ETC再セットアップとセットアップ証明書の確認方法｜独立解説',
      desc: 'ETC再セットアップが必要なケース、費用と所要時間、ETCセットアップ証明書の役割と紛失時の対応、車載器管理番号の確認方法、ETC利用照会サービス・ETCマイレージへの反映を、Japan Toll Receiptsが中立に整理した独立解説です。',
      keywords: 'ETC再セットアップ,ETCセットアップ証明書,ETCセットアップ証明書 再発行,ETC車載器管理番号,ETC車載器管理番号 確認方法,ETC再セットアップ 費用,ETC再セットアップ ナンバープレート変更,ETC再セットアップ ディーラー カー用品店,ETC利用照会サービス 登録 車載器管理番号,ETCマイレージ 車両情報変更',
    },
    en: {
      title: 'ETC Re-Setup & Setup Certificate — Independent Reference',
      desc: 'Independent neutral reference covering when ETC re-setup is required, cost and duration, the role of the ETC setup certificate, recovery of the OBU management number, and how ETC Usage Inquiry Service (MEISAI) Inquiry Service and ETC Mileage registrations are affected.',
      keywords: 'Japan ETC re-setup,Japan ETC setup certificate,Japan ETC OBU management number,Japan ETC plate change,Japan ETC vehicle change',
    },
  },
  '/guides/etc-resetup-setup-certificate-en-guide': {
    ja: {
      title: 'ETC再セットアップとセットアップ証明書の確認方法｜独立解説',
      desc: 'ETC再セットアップが必要なケース、費用と所要時間、ETCセットアップ証明書の役割と紛失時の対応、車載器管理番号の確認方法、ETC利用照会サービス・ETCマイレージへの反映を、Japan Toll Receiptsが中立に整理した独立解説です。',
      keywords: 'ETC再セットアップ,ETCセットアップ証明書,ETCセットアップ証明書 再発行,ETC車載器管理番号,ETC車載器管理番号 確認方法,ETC再セットアップ 費用,ETC再セットアップ ナンバープレート変更,ETC再セットアップ ディーラー カー用品店,ETC利用照会サービス 登録 車載器管理番号,ETCマイレージ 車両情報変更',
    },
    en: {
      title: 'ETC Re-Setup & Setup Certificate — Independent Reference',
      desc: 'Independent neutral reference covering when ETC re-setup is required, cost and duration, the role of the ETC setup certificate, recovery of the OBU management number, and how ETC Usage Inquiry Service (MEISAI) Inquiry Service and ETC Mileage registrations are affected.',
      keywords: 'Japan ETC re-setup,Japan ETC setup certificate,Japan ETC OBU management number,Japan ETC plate change,Japan ETC vehicle change',
    },
  },
  // Iter 283 (2026-05-24) — ETCマイレージ login / registration troubleshooting guide
  '/guides/etc-mileage-login-registration-troubleshooting-guide': {
    ja: {
      title: 'ETCマイレージ登録・ログインできないときの確認手順｜独立解説',
      desc: 'ETCマイレージサービスに登録できない・ログインできない・パスワードを忘れたなどのトラブルで、自分でできる確認手順を、Japan Toll Receiptsが中立に整理した独立サポートガイドです。',
      keywords: 'ETCマイレージ 登録できない,ETCマイレージ ログインできない,ETCマイレージ パスワード忘れた,ETCマイレージ ユーザーID 忘れた,ETCマイレージ メールアドレス変更,ETCマイレージ 退会,ETCマイレージ 車載器管理番号,ETCマイレージ 登録方法,ETCマイレージ お問い合わせ',
    },
    en: {
      title: 'ETC Mileage Login & Registration Troubleshooting — Independent Reference',
      desc: 'Independent neutral troubleshooting reference for users who cannot register, cannot log in, or have forgotten their password on the ETC Mileage Service, covering self-serve checks before contacting the service office.',
      keywords: 'Japan ETC Mileage login,Japan ETC Mileage registration,Japan ETC Mileage forgot password,Japan ETC Mileage forgot user ID,Japan ETC Mileage troubleshoot',
    },
  },
  '/guides/etc-mileage-login-registration-troubleshooting-en-guide': {
    ja: {
      title: 'ETCマイレージ登録・ログインできないときの確認手順｜独立解説',
      desc: 'ETCマイレージサービスに登録できない・ログインできない・パスワードを忘れたなどのトラブルで、自分でできる確認手順を、Japan Toll Receiptsが中立に整理した独立サポートガイドです。',
      keywords: 'ETCマイレージ 登録できない,ETCマイレージ ログインできない,ETCマイレージ パスワード忘れた,ETCマイレージ ユーザーID 忘れた,ETCマイレージ メールアドレス変更,ETCマイレージ 退会,ETCマイレージ 車載器管理番号,ETCマイレージ 登録方法,ETCマイレージ お問い合わせ',
    },
    en: {
      title: 'ETC Mileage Login & Registration Troubleshooting — Independent Reference',
      desc: 'Independent neutral troubleshooting reference for users who cannot register, cannot log in, or have forgotten their password on the ETC Mileage Service, covering self-serve checks before contacting the service office.',
      keywords: 'Japan ETC Mileage login,Japan ETC Mileage registration,Japan ETC Mileage forgot password,Japan ETC Mileage forgot user ID,Japan ETC Mileage troubleshoot',
    },
  },
  // Iter 284 (2026-05-24) — ETC利用照会サービス registration walkthrough
  '/guides/etc-riyou-shoukai-service-registration-guide': {
    ja: {
      title: 'ETC利用照会サービスの登録方法と必要情報｜独立解説',
      desc: 'ETC利用照会サービスの新規登録手順（8ステップ）、必要なETCカード番号・車載器管理番号・本人情報・車両情報、個人と法人の違い、登録時のつまずきポイントを、Japan Toll Receiptsが中立に整理した独立ガイドです。',
      keywords: 'ETC利用照会サービス 登録方法,ETC利用照会サービス 新規登録,ETC利用照会サービス 法人登録,ETC利用照会サービス 個人,ETC利用照会サービス 必要なもの,ETC利用照会サービス 車載器管理番号,ETC利用照会サービス ETCカード番号,ETC利用照会サービス 退会,ETC利用照会サービス 認証メール',
    },
    en: {
      title: 'ETC Usage Inquiry Service Registration Walkthrough — Independent Reference',
      desc: 'Step-by-step independent reference covering new registration on the official ETC Usage Inquiry Service, personal vs corporate, common stumbling points, and what becomes available after registration.',
      keywords: 'Japan ETC Usage Inquiry Service registration,Japan MEISAI registration,Japan ETC card register MEISAI,Japan OBU management number MEISAI registration',
    },
  },
  '/guides/etc-usage-inquiry-service-registration-guide': {
    ja: {
      title: 'ETC利用照会サービスの登録方法と必要情報｜独立解説',
      desc: 'ETC利用照会サービスの新規登録手順（8ステップ）、必要なETCカード番号・車載器管理番号・本人情報・車両情報、個人と法人の違い、登録時のつまずきポイントを、Japan Toll Receiptsが中立に整理した独立ガイドです。',
      keywords: 'ETC利用照会サービス 登録方法,ETC利用照会サービス 新規登録,ETC利用照会サービス 法人登録,ETC利用照会サービス 個人,ETC利用照会サービス 必要なもの,ETC利用照会サービス 車載器管理番号,ETC利用照会サービス ETCカード番号,ETC利用照会サービス 退会,ETC利用照会サービス 認証メール',
    },
    en: {
      title: 'ETC Usage Inquiry Service Registration Walkthrough — Independent Reference',
      desc: 'Step-by-step independent reference covering new registration on the official ETC Usage Inquiry Service, personal vs corporate, common stumbling points, and what becomes available after registration.',
      keywords: 'Japan ETC Usage Inquiry Service registration,Japan MEISAI registration,Japan ETC card register MEISAI,Japan OBU management number MEISAI registration',
    },
  },
  // Wave 1 / Iter 285 (2026-05-24) — ETC利用明細の保存期間ガイド
  '/guides/etc-meisai-hozon-kikan-guide': {
    ja: { title: 'ETC利用明細の保存期間と必要な対応｜独立解説', desc: 'ETC利用照会サービスの15か月、ETCコーポレートカードの62日、電子帳簿保存法の保存期間など、ETC利用明細・利用証明書の保存期間を、Japan Toll Receiptsが中立に整理した独立解説です。', keywords: 'ETC利用明細 保存期間,ETC明細 何ヶ月,ETC利用照会サービス 15ヶ月,ETC コーポレートカード 62日,ETC明細 過去,ETC明細 長期保存,電帳法 ETC' },
    en: { title: 'ETC Statement Retention Periods — Independent Reference', desc: 'Independent neutral reference covering retention windows on the ETC Usage Inquiry Service (~15 months), ETC Corporate Cards (~62 days), and Japan&apos;s Electronic Books Preservation Act.', keywords: 'Japan ETC statement retention,Japan ETC 15 months,Japan ETC corporate card 62 days,Japan ETC long-term storage' },
  },
  '/guides/etc-statement-retention-period-guide': {
    ja: { title: 'ETC利用明細の保存期間と必要な対応｜独立解説', desc: 'ETC利用照会サービスの15か月、ETCコーポレートカードの62日、電子帳簿保存法の保存期間など、ETC利用明細・利用証明書の保存期間を、Japan Toll Receiptsが中立に整理した独立解説です。', keywords: 'ETC利用明細 保存期間,ETC明細 何ヶ月,ETC利用照会サービス 15ヶ月,ETC コーポレートカード 62日' },
    en: { title: 'ETC Statement Retention Periods — Independent Reference', desc: 'Independent neutral reference covering retention windows on the ETC Usage Inquiry Service (~15 months), ETC Corporate Cards (~62 days), and Japan&apos;s Electronic Books Preservation Act.', keywords: 'Japan ETC statement retention,Japan ETC 15 months,Japan ETC corporate card 62 days,Japan ETC long-term storage' },
  },
  // Wave 1 / Iter 286 (2026-05-24) — 高速代の消費税ガイド
  '/guides/koutsuhi-shouhizei-etc-meisai-guide': {
    ja: { title: '高速代の消費税とETC利用明細の確認ポイント｜独立解説', desc: '高速道路料金にかかる消費税の扱い、ETC利用明細・利用証明書での確認ポイント、適格簡易請求書としての扱いを、Japan Toll Receiptsが中立に整理した独立解説です。', keywords: '高速代 消費税,高速料金 消費税,ETC 消費税,高速道路 消費税,適格請求書 ETC,適格簡易請求書 ETC' },
    en: { title: 'Highway-Toll Consumption Tax — Independent Reference', desc: 'Independent neutral reference covering consumption-tax treatment of Japanese highway tolls, how to verify it on ETC statements and usage certificates, and qualified-invoice handling.', keywords: 'Japan highway toll consumption tax,Japan ETC consumption tax,Japan qualified invoice highway toll' },
  },
  '/guides/highway-toll-consumption-tax-etc-statement-guide': {
    ja: { title: '高速代の消費税とETC利用明細の確認ポイント｜独立解説', desc: '高速道路料金にかかる消費税の扱い、ETC利用明細・利用証明書での確認ポイント、適格簡易請求書としての扱いを、Japan Toll Receiptsが中立に整理した独立解説です。', keywords: '高速代 消費税,高速料金 消費税,ETC 消費税' },
    en: { title: 'Highway-Toll Consumption Tax — Independent Reference', desc: 'Independent neutral reference covering consumption-tax treatment of Japanese highway tolls, how to verify it on ETC statements and usage certificates, and qualified-invoice handling.', keywords: 'Japan highway toll consumption tax,Japan ETC consumption tax,Japan qualified invoice highway toll' },
  },
  // Wave 1 / Iter 287 (2026-05-24) — 高速代の仕訳ガイド
  '/guides/koutsuhi-shiwake-etc-meisai-guide': {
    ja: { title: '高速代の仕訳例とETC利用明細の確認ポイント｜独立解説', desc: '法人・個人事業主が高速代を仕訳するときの代表的な勘定科目（旅費交通費・車両関係費 等）、ETC利用明細・利用証明書での確認ポイント、社用車と私用利用の切り分けを、Japan Toll Receiptsが中立に整理した独立解説です。', keywords: '高速代 仕訳,高速料金 仕訳,ETC 仕訳,旅費交通費 ETC,車両関係費 高速,事業主貸 高速代,経費精算 高速代 仕訳' },
    en: { title: 'Highway-Toll Journal Entries — Independent Reference', desc: 'Independent neutral reference showing journal-entry examples for highway tolls, what to verify on ETC statements, and how to separate business vs personal use.', keywords: 'Japan highway toll journal entry,Japan ETC journal entry,Japan travel expenses ETC,Japan vehicle expense highway' },
  },
  '/guides/highway-toll-journal-entry-etc-statement-guide': {
    ja: { title: '高速代の仕訳例とETC利用明細の確認ポイント｜独立解説', desc: '法人・個人事業主が高速代を仕訳するときの代表的な勘定科目、ETC利用明細・利用証明書での確認ポイント、社用車と私用利用の切り分けを、Japan Toll Receiptsが中立に整理した独立解説です。', keywords: '高速代 仕訳,高速料金 仕訳,ETC 仕訳,旅費交通費 ETC' },
    en: { title: 'Highway-Toll Journal Entries — Independent Reference', desc: 'Independent neutral reference showing journal-entry examples for highway tolls, what to verify on ETC statements, and how to separate business vs personal use.', keywords: 'Japan highway toll journal entry,Japan ETC journal entry,Japan travel expenses ETC' },
  },
  // Wave 1 / Iter 288 (2026-05-24) — ETC明細と電子帳簿保存法ガイド
  '/guides/etc-meisai-denshi-chobo-hozon-hou-guide': {
    ja: { title: 'ETC利用明細と電子帳簿保存法の対応ポイント｜独立解説', desc: '電子帳簿保存法（電帳法）の電子取引データ保存要件（真実性・検索性・可視性）と、ETC利用明細・利用証明書PDFの保存運用ポイントを、Japan Toll Receiptsが中立に整理した独立解説です。', keywords: 'ETC明細 電子帳簿保存法,電帳法 ETC,ETC 電帳法,電子取引 ETC,検索性 ETC明細,真実性 ETC明細,可視性 ETC明細,ETC PDF 電帳法,事務処理規程 ETC' },
    en: { title: 'ETC Statements & the e-Storage Act — Independent Reference', desc: 'Independent neutral reference covering Japan&apos;s Electronic Books Preservation Act (denshi-chobo) requirements for electronic-transaction records, and how ETC statements + usage certificates fit into them.', keywords: 'Japan e-Storage Act ETC,Japan denshi chobo ETC,Japan electronic transaction ETC,Japan ETC PDF storage' },
  },
  '/guides/etc-statement-e-storage-law-guide': {
    ja: { title: 'ETC利用明細と電子帳簿保存法の対応ポイント｜独立解説', desc: '電子帳簿保存法（電帳法）の電子取引データ保存要件と、ETC利用明細・利用証明書PDFの保存運用ポイントを、Japan Toll Receiptsが中立に整理した独立解説です。', keywords: 'ETC明細 電子帳簿保存法,電帳法 ETC,ETC 電帳法,電子取引 ETC' },
    en: { title: 'ETC Statements & the e-Storage Act — Independent Reference', desc: 'Independent neutral reference covering Japan&apos;s Electronic Books Preservation Act (denshi-chobo) requirements for electronic-transaction records, and how ETC statements + usage certificates fit into them.', keywords: 'Japan e-Storage Act ETC,Japan denshi chobo ETC,Japan electronic transaction ETC,Japan ETC PDF storage' },
  },
  // Wave 2 / Iter 289 (2026-05-24) — ETC明細 表示されない + 過去分 (combined)
  '/guides/etc-meisai-hyoji-sarenai-kako-guide': {
    ja: { title: 'ETC利用明細が表示されない・過去分を確認する方法｜独立解説', desc: 'ETC利用照会サービスでETC利用明細・利用履歴が表示されないときの確認手順、15か月／62日を超える過去分の扱いを、Japan Toll Receiptsが中立に整理した独立サポートガイドです。', keywords: 'ETC利用明細 表示されない,ETC利用履歴 表示されない,ETC明細 出ない,ETC利用照会サービス 明細 出ない,ETC利用明細 過去分,ETC明細 過去 確認,ETC コーポレートカード 過去分' },
    en: { title: 'ETC Statement Not Showing & Historical Records — Independent Reference', desc: 'Independent neutral troubleshooting reference for ETC Usage Inquiry Service (MEISAI) Inquiry Service display issues, plus handling records older than the 15-month / 62-day retention windows.', keywords: 'Japan ETC statement not showing,Japan ETC history not displayed,Japan ETC historical records' },
  },
  '/guides/etc-statement-not-showing-historical-guide': {
    ja: { title: 'ETC利用明細が表示されない・過去分を確認する方法｜独立解説', desc: 'ETC利用照会サービスでETC利用明細・利用履歴が表示されないときの確認手順、15か月／62日を超える過去分の扱いを、Japan Toll Receiptsが中立に整理した独立サポートガイドです。', keywords: 'ETC利用明細 表示されない,ETC利用履歴 表示されない,ETC明細 出ない,ETC利用照会サービス 明細 出ない,ETC利用明細 過去分,ETC明細 過去 確認' },
    en: { title: 'ETC Statement Not Showing & Historical Records — Independent Reference', desc: 'Independent neutral troubleshooting reference for ETC Usage Inquiry Service (MEISAI) Inquiry Service display issues, plus handling records older than the 15-month / 62-day retention windows.', keywords: 'Japan ETC statement not showing,Japan ETC history not displayed,Japan ETC historical records' },
  },
  // Wave 6 / Iter 290 (2026-05-24) — 経費精算スターターキット cluster-hub
  '/guides/etc-keihi-seisan-starter-kit-guide': {
    ja: { title: 'ETC利用記録を経費精算で使うための全体像｜独立ハブ', desc: 'ETC車載器のセットアップから、ETC利用照会サービスの登録、日々の通行、ETC利用明細・利用証明書の整理、経費精算・インボイス対応までの全体像を、Japan Toll Receiptsが中立に案内する独立ハブです。', keywords: 'ETC 経費精算 流れ,ETC 経費精算 全体像,ETC 経費精算 スターターキット,ETC 経費精算 始め方,ETC 経費精算 案内,ETC明細 経費精算 ガイド,ETC利用記録 経費精算' },
    en: { title: 'ETC Expense Report Starter Kit — Independent Navigation Hub', desc: 'Independent navigation hub connecting OBU setup, ETC Usage Inquiry Service (MEISAI) Inquiry Service registration, daily travel, statement organisation, and reimbursement / qualified-invoice handling.', keywords: 'Japan ETC expense report flow,Japan ETC starter kit,Japan ETC reimbursement guide,Japan ETC qualified invoice flow' },
  },
  '/guides/etc-expense-report-starter-kit-guide': {
    ja: { title: 'ETC利用記録を経費精算で使うための全体像｜独立ハブ', desc: 'ETC車載器のセットアップから、ETC利用照会サービスの登録、日々の通行、ETC利用明細・利用証明書の整理、経費精算・インボイス対応までの全体像を、Japan Toll Receiptsが中立に案内する独立ハブです。', keywords: 'ETC 経費精算 流れ,ETC 経費精算 全体像,ETC 経費精算 スターターキット,ETC 経費精算 始め方' },
    en: { title: 'ETC Expense Report Starter Kit — Independent Navigation Hub', desc: 'Independent navigation hub connecting OBU setup, ETC Usage Inquiry Service (MEISAI) Inquiry Service registration, daily travel, statement organisation, and reimbursement / qualified-invoice handling.', keywords: 'Japan ETC expense report flow,Japan ETC starter kit,Japan ETC reimbursement guide,Japan ETC qualified invoice flow' },
  },
  '/etc-phishing-safety': {
    ja: {
      title: 'ETC利用照会サービスを装った不審メール・詐欺対策ガイド',
      desc: '「ETC利用照会サービス」を装った詐欺メール・SMSが急増しています。本物との見分け方、不審メールの典型例、被害に遭った場合の対処法、安全な利用のポイントを解説。',
      keywords: 'ETC不審メール,ETC利用照会サービス 詐欺,ETCフィッシング,ETC SMS 詐欺,ETC偽メール 見分け方',
    },
    en: {
      title: 'ETC Phishing & Scam Safety Guide for Japan Drivers',
      desc: 'Stay safe from fake "ETC Inquiry Service" emails and SMS scams targeting Japan drivers. Identify red flags, learn the legitimate domain whitelist, and find out what to do if you click a phishing link by mistake.',
      keywords: 'Japan ETC phishing,fake ETC email Japan,Japan ETC scam SMS,Japan toll phishing safety',
    },
  },
  '/etc-riyou-rireki-printer-guide': {
    ja: {
      title: 'ETC利用履歴発行プリンター 使い方ガイド｜全国SA・PA設置場所',
      desc: 'ETC利用履歴発行プリンターの使い方、設置されている全国の高速道路SA・PA一覧、印刷可能期間(15か月)、印刷できる項目、印刷できない場合のトラブル対処法を解説。',
      keywords: 'ETC利用履歴発行プリンター,ETC履歴 印刷 SA,ETC履歴 印刷 PA,ETC利用履歴 設置場所',
    },
    en: { title: 'ETC Usage History Printer Kiosks at Japan Service Areas — Guide', desc: 'How to use ETC usage-history printer kiosks at Japan highway service areas (SA/PA). Locations, 15-month coverage window, what gets printed, and what to do when it errors.', keywords: 'Japan ETC printer kiosk,Japan SA PA ETC printer,Japan ETC history print' },
  },
  '/etc-usage-history-printer-guide': {
    ja: { title: 'ETC利用履歴プリンター(英語版)｜JapanTollReceipts.jp', desc: '高速道路SA・PAのETC利用履歴発行プリンター英語ガイド。', keywords: 'ETC printer guide' },
    en: {
      title: 'ETC Usage History Printers at Japan Service Areas | English Guide',
      desc: 'Complete English guide to the ETC printer kiosks at Japan highway service areas (SA / PA). Find one near your route, understand the 15-month limit, troubleshoot errors, and know when to use Japan Toll Receipts instead.',
      keywords: 'Japan ETC usage history printer,Japan SA PA printer kiosk,how to print ETC history Japan',
    },
  },

  // ── Sitemap page ────────────────────────────────────────────────
  '/sitemap': {
    ja: { title: 'サイトマップ｜全ページ一覧｜Japan Toll Receipts', desc: 'Japan Toll Receiptsの全公開ページ一覧です。料金、機能、ガイド、法的文書、サポートページへのリンクをまとめています。', keywords: 'Japan Toll Receipts サイトマップ' },
    en: { title: 'Sitemap | All Pages | Japan Toll Receipts', desc: 'Complete page directory for Japan Toll Receipts. Pricing, features, guides, legal documents, and support — all linked from one page.', keywords: 'Japan Toll Receipts sitemap' },
  },

  // ── Changelog / Release notes ───────────────────────────────────
  '/changelog': {
    ja: { title: '更新履歴｜リリースノート（JST）｜Japan Toll Receipts', desc: 'Japan Toll Receiptsのリリースノートです。新機能、改善、修正の履歴を日本標準時（JST）で記録しています。', keywords: 'Japan Toll Receipts 更新履歴,リリースノート,ETCサービス アップデート' },
    en: { title: 'Changelog | Release Notes (JST) | Japan Toll Receipts', desc: 'Japan Toll Receipts release notes — every feature, improvement, and fix logged in Japan Standard Time (JST). Early Access, architecture trust center, AI operations, and Government resources.', keywords: 'Japan Toll Receipts changelog,release notes Japan ETC,ETC service updates' },
  },

  // ── Live Reporting — public daily delivery-reliability tracker ───
  '/live-reporting': {
    ja: { title: 'ライブレポート｜毎日のETC配信実績を公開｜Japan Toll Receipts', desc: 'Japan Toll Receiptsのライブレポート。毎朝7時（JST）に実際のETCカードを処理し、照合率・公式書類（利用証明書PDF・明細PDF・CSV）・自動復旧・精度・処理時間をサニタイズした集計値で毎日公開します。現在は14日間バーンインを実施中で、以降も継続して実績を記録します。誤配信ゼロ・個人情報非掲載。', keywords: 'Japan Toll Receipts ライブレポート,ETC配信 実績,毎日配信 検証,ETC明細 自動配信 実績,配信精度,稼働状況' },
    en: { title: 'Live Reporting | Public Daily ETC Delivery Tracker | Japan Toll Receipts', desc: 'Japan Toll Receipts live reporting. Every morning at 07:00 JST we process real ETC cards and publish sanitized, aggregate results — reconciliation %, official documents (usage certificate PDF, detail PDF, CSV), self-healed recoveries, accuracy and run time. Currently in a 14-day burn-in and recorded daily thereafter. Zero false reports, no personal data shown.', keywords: 'Japan Toll Receipts live reporting,ETC delivery reliability,daily delivery results,ETC report accuracy,uptime tracker Japan ETC,reconciliation' },
  },

  // ── Help / Support hub ──────────────────────────────────────────
  '/help': {
    ja: { title: 'ヘルプ・サポート｜FAQ・ポリシー・お問い合わせ｜Japan Toll Receipts', desc: 'Japan Toll Receiptsのサポートページです。よくある質問、ポリシー、安全性、会社情報、メール・電話サポートへの導線をまとめています。', keywords: 'Japan Toll Receipts ヘルプ,ETCサポート,FAQ,お問い合わせ' },
    en: { title: 'Help & Support Portal | FAQ, Policies & Contact — Japan Toll Receipts', desc: 'Japan Toll Receipts support hub: FAQ, Policies, Safety & Security, and Company info — one-stop access to email and phone support.', keywords: 'Japan Toll Receipts help,ETC support hub,FAQ Japan ETC,contact support Japan' },
  },

  // ── Policies & Procedures (POL-01 to POL-10) ────────────────────
  '/policies': {
    ja: { title: 'ポリシーと手順（POL-01〜POL-10）｜Japan Toll Receipts', desc: '当社の運営方針、料金プラン、ETC利用照会サービス（MEISAI）アカウント接続ルール、メール変更手順、アカウント閉鎖、ETCカード形式、品質管理、返金、データ保持について説明しています。', keywords: 'Japan Toll Receipts ポリシー,POL-01,ETC運営ルール,MEISAI接続ルール,データ保持ポリシー' },
    en: { title: 'Policies & Procedures (POL-01 to POL-10) | Japan Toll Receipts', desc: 'Banking-grade public reference: our promises, plans, ETC Usage Inquiry Service (MEISAI) connection rules, email-change procedure, account closure, ETC card formats, quarantine rules, refunds, and data retention.', keywords: 'Japan Toll Receipts policies,POL-01,Japan ETC operating policy,MEISAI connection policy,data retention policy' },
  },

  // ── Market Opportunity Brief ────────────────────────────────────
  '/market': {
    ja: { title: '市場ニーズレポート｜日本のETC利用明細市場とドライバー数', desc: '日本のETC利用市場、ドライバー人口、経費精算ニーズに関するレポートです。統計、顧客ペルソナ、5年予測、将来見通しに関する注意事項を掲載しています。', keywords: 'ETC市場規模,日本 ETCドライバー数,ETC経費精算 市場' },
    en: { title: 'Market Opportunity Brief | Japan ETC & Toll Reporting', desc: 'Publication-grade brief on the Japan ETC and toll reporting market. Cited statistics, customer personas, five-year forecast chart, and forward-looking disclaimer.', keywords: 'Japan ETC market opportunity,Japan toll market size,ETC reporting market Japan' },
  },

  // ── Why JTR / Early Access Program / Official Resources ──────────────
  '/why': {
    ja: { title: 'Japan Toll Receiptsを選ぶ理由｜ETCレポート自動配信', desc: 'Japan Toll Receiptsを選ぶ理由をご紹介。親会社NoJo EnterpriseのETC運営実績(2013年〜)、独立運営、取得後すぐに配信する方式、経費精算や社内確認にも使いやすいPDF・CSVレポート、日本語・英語のバイリンガル対応、メール自動配信による月数時間の時間節約。NEXCO・ETC利用照会サービスとは別の独立サービスです。', keywords: 'ETC 自動化 メリット,Japan Toll Receipts 強み,ETC利用明細 メール配信,独立運営 ETCサービス,ETCレポート 自動' },
    en: { title: 'Why Japan Toll Receipts | Built by Drivers, for Drivers', desc: 'Why drivers and businesses choose Japan Toll Receipts: parent company NoJo Enterprise (operating in ETC since 2013), retrieve-and-deliver architecture, reports organized for review, bilingual JA/EN, and the time we save your team every month.', keywords: 'why Japan Toll Receipts,Japan ETC service comparison,Japan toll automation advantages' },
  },
  '/founder-program': {
    ja: { title: '1年間のプレミアム無料体験｜先行利用プログラム｜Japan Toll Receipts', desc: '個人向け体験枠は先着100,000件限定。Daily Plusの対象機能を1年間無料で試せます。クレジットカード登録不要。終了後はFree Weeklyへ自動移行します。', keywords: '1年間無料体験,先行利用プログラム,先着100000アカウント,ETCプレミアム体験,Daily Plus無料体験' },
    en: { title: '1-Year Premium Free Trial | First 100,000 Members | Japan Toll Receipts', desc: 'Limited to the first 100,000 verified members: 1 full year of Daily Plus premium, no credit card. Auto-reverts to Free Weekly after the year — no charges, ever.', keywords: 'Japan ETC founder program,1-year free premium Japan,first 100000 ETC' },
  },
  '/official-etc-resources': {
    ja: { title: '日本の公式ETC関連リソース集｜NEXCO・ETC利用照会サービス・道路事業者', desc: 'ETC利用照会サービス（MEISAI）、NEXCO東日本・中日本・西日本、首都高速、阪神高速、本四高速など、日本のETC関連公式ページへのリンクをまとめています。', keywords: 'ETC公式リソース,ETC利用照会サービス 公式,NEXCO 公式,首都高速 公式,阪神高速 公式' },
    en: { title: 'Official Japan ETC Resources | NEXCO, the ETC Usage Inquiry Service & Road Operators', desc: 'Curated links to official Japan ETC resources: ETC Inquiry Service (MEISAI), NEXCO East/Central/West, Metropolitan Expressway, Hanshin Expressway, and Honshi-Shikoku Bridge.', keywords: 'official Japan ETC resources,NEXCO official,MEISAI official link,Japan road operators' },
  },
  '/guides/etc-card-number-formats': {
    ja: { title: 'ETCカード番号フォーマットガイド｜桁数と入力方法', desc: 'ETCカード番号の桁数、表記形式、カード会社ごとの違い、ETC利用照会サービス（MEISAI）登録時の入力エラーを避ける方法、カード番号の確認方法を解説します。', keywords: 'ETCカード番号,ETCカード フォーマット,ETCカード 桁数,ETCカード 番号確認' },
    en: { title: 'ETC Card Number Formats — Complete Guide | Japan Toll Receipts', desc: 'Complete guide to Japan ETC card number formats: digit counts, issuer-specific layouts, common entry errors, and how to verify your card number before ETC Usage Inquiry Service (MEISAI) registration.', keywords: 'Japan ETC card number,ETC card format Japan,ETC card digits,Japan ETC card number lookup' },
  },
  // ── Government & Public-Sector track — overview meta. JTR is independent:
  //    not affiliated with any government agency, NEXCO, the ETC Usage Inquiry
  //    Service (MEISAI), ITS-TEA, or any toll operator. Wording stays neutral and
  //    avoids tax/legal/audit/fraud/compliance guarantees.
  '/government': {
    ja: {
      title: '官公庁・公共部門向けETC利用記録の整理｜承認車両との照合 | JTR',
      desc: '官公庁・公共部門の組織が、ETC利用記録を承認車両リストと照合し、想定外・未承認車両の利用の可能性を確認するための民間サービス。JTRは独立した民間事業者であり、いかなる政府機関・NEXCO・ETC利用照会サービス（MEISAI）・ITS-TEAとも関係がありません。',
      keywords: '官公庁 ETC,公共部門 ETC,公用車 ETC 記録,承認車両 照合,未承認車両 確認,公用車 ETC レポート,JTR 民間サービス',
    },
    en: {
      title: 'Government & Public-Sector ETC Usage Records | JTR',
      desc: 'A service that helps government and public-sector organizations organize ETC usage records and compare recorded vehicle information against their approved vehicle registry to surface possible mismatches for human review. JTR is independent — not affiliated with any government agency.',
      keywords: 'government ETC Japan,public-sector ETC,approved vehicle registry,possible unapproved vehicle use,official vehicle ETC records,JTR independent service',
    },
  },

  '/government/features': {
    ja: {
      title: '官公庁・公共部門向け機能｜ETC利用記録と承認車両の照合 | JTR',
      desc: '官公庁・公共部門の組織向け、ETC利用記録を整理し承認車両リストと照合する機能。PDF + CSVレポート、承認車両との照合（要確認フラグ）、月次サマリーに対応。JTRは独立した民間サービスです。',
      keywords: '官公庁 ETC機能,公共部門 ETC機能,ETC利用記録 PDF CSV,承認車両 照合 ETC,月次 ETCサマリー',
    },
    en: {
      title: 'Government & Public-Sector Features — ETC Usage Records | JTR',
      desc: 'Features for government and public-sector ETC usage record organization: PDF + CSV records, comparison against the approved vehicle registry (needs-review only), monthly summaries. JTR is independent.',
      keywords: 'government ETC features,public-sector ETC features,ETC usage PDF CSV,approved vehicle comparison,monthly ETC summary',
    },
  },

  // ── Government — approved-vehicle comparison (needs-review only) ──
  '/government/authorized-vehicles': {
    ja: {
      title: '承認車両リストとの照合（要確認フラグ）｜官公庁・公共部門 | JTR',
      desc: '官公庁・公共部門の組織が、ETC利用記録を整理し承認車両リストと照合するためのJTR民間サービス。リストにない車両の利用は「要確認」としてフラグされ、人によるレビューを支援します。フラグは未承認利用を断定するものではありません。JTRは独立した民間事業者です。',
      keywords: '官公庁 承認車両 リスト,公共部門 登録車両,要確認フラグ,ETC利用記録 照合,JTR 民間サービス',
    },
    en: {
      title: 'Approved-Vehicle Comparison | Government & Public-Sector ETC | JTR',
      desc: 'For government and public-sector organizations in Japan: organize ETC usage records and surface activity from vehicles not on your approved vehicle registry as needs-review flags for human review. A flag does not establish unauthorized use. JTR is independent — not affiliated with any government agency.',
      keywords: 'government approved vehicle registry,public-sector registered vehicles,needs-review flags Japan,ETC usage comparison,JTR vehicle review',
    },
  },

  // ── Government — plate type reference page ──────────────────────
  '/government/plate-types': {
    ja: {
      title: '日本の公用車・ナンバープレート種別リファレンス | JTR',
      desc: 'ETCカードが使われる日本の主要なナンバープレート区分（一般・事業用・公用車を含む）のリファレンス。JTRの承認車両リストに登録し、ETC利用記録と照合できます。すべての特殊用途プレートへの対応を保証するものではありません。',
      keywords: '日本 ナンバープレート 区分,公用車 ETC,プレート種別 ETC,承認車両 照合',
    },
    en: {
      title: 'Japan Vehicle Plate Types Reference for ETC Records | JTR',
      desc: 'Reference for the main Japan license-plate categories that use an ETC card (including ordinary, commercial, and official vehicles). All registrable in the JTR Approved Vehicle Registry for comparison against ETC usage. Recognition of every special-purpose plate is not guaranteed.',
      keywords: 'Japan license plate categories,official vehicle ETC Japan,plate types ETC,approved vehicle comparison',
    },
  },

  '/government/pricing': {
    ja: {
      title: '官公庁・公共部門向け料金｜ETC利用記録プラン | JTR',
      desc: '官公庁・公共部門の組織向け、ETC利用記録の整理・承認車両照合プラン。JTRは独立した民間サービスで、いかなる政府機関からの公的承認も示すものではありません。',
      keywords: '官公庁 ETC 料金,公共部門 ETC 料金,ETC利用記録 プラン,承認車両 照合 料金,JTR 民間 料金',
    },
    en: {
      title: 'Government & Public-Sector Pricing — ETC Record Plans | JTR',
      desc: 'ETC usage record organization and approved-vehicle-comparison plans for government and public-sector organizations in Japan. JTR is independent — references to use cases do not indicate government endorsement.',
      keywords: 'government ETC pricing,public-sector ETC pricing,ETC usage record plan,approved vehicle comparison pricing,JTR plan',
    },
  },

  // ── Government & Public-Sector reference cluster (neutral rewrites) ──
  '/government-vehicle-toll-resources-japan': {
    ja: {
      title: '公用車 ETC 記録の整理リソース｜承認車両との照合 | JTR',
      desc: '官公庁・自治体・公的機関向けの中立的リファレンス。ETC利用記録の整理、承認車両リストとの照合、調達・プライバシー・保管の検討事項を解説。JTRは独立した民間サービスです。',
      keywords: '公用車 ETC リソース,官公庁 ETC 記録,承認車両 照合,公共部門 ETC,JTR 民間サービス',
    },
    en: {
      title: 'Government Vehicle Toll Resources — ETC Record Reference | JTR',
      desc: 'A neutral reference for government and public-sector organizations: organizing ETC usage records, comparing against an approved vehicle registry, and procurement / privacy / retention considerations. JTR is independent.',
      keywords: 'government vehicle toll resources,public-sector ETC records,approved vehicle registry,ETC record organization Japan,JTR independent service',
    },
  },
  '/government-official-vehicle-tolls-japan': {
    ja: {
      title: '公用車 ETC 記録管理｜承認車両との照合・要確認レビュー | JTR',
      desc: '公用車のETC利用記録をカード別・車両別・部署別に整理し、承認車両リストと照合。リスト外は要確認として人によるレビューへ。JTRは独立した民間サービスです。',
      keywords: '公用車 ETC 記録管理,承認車両 照合,公用車 ETC 明細,部署別 ETC,JTR 民間サービス',
    },
    en: {
      title: 'Government Vehicle ETC Record Management | JTR',
      desc: 'Organize official-vehicle ETC usage by card, vehicle, and department, and compare it against your approved vehicle registry — off-list activity surfaced for human review. JTR is independent.',
      keywords: 'government vehicle ETC record management,official vehicle ETC records,approved vehicle comparison,public-sector ETC,JTR',
    },
  },
  '/government-fleet-etc-recordkeeping-japan': {
    ja: {
      title: '公用車 ETC 記録管理・保管ガイド｜レビュー履歴・PDF/CSV | JTR',
      desc: '公的機関の複数車両運用向け、ETC利用記録の整理・保管・レビュー履歴の残し方。取得データを継続保存しないパススルー設計。JTRは独立した民間サービスです。',
      keywords: '公用車 ETC 記録保管,ETC レビュー履歴,PDF CSV 記録,公共部門 ETC 保管,JTR',
    },
    en: {
      title: 'Government Fleet ETC Recordkeeping — Retention & Review Log | JTR',
      desc: 'Organize, retain, and keep a review history of ETC usage for public-sector fleets. Pass-through design with no long-term toll-data store. JTR is independent.',
      keywords: 'government fleet ETC recordkeeping,ETC retention review log,PDF CSV records,public-sector ETC storage,JTR',
    },
  },
  '/who-pays-government-vehicle-tolls-japan': {
    ja: {
      title: '公用車の通行料は誰が払う?｜一般情報ガイド | JTR',
      desc: '公用車の通行料の支払い・精算に関する一般的な考え方を中立的に解説（法的・行政上の助言ではありません）。JTRは支払いではなく記録の整理と照合を支援する独立した民間サービスです。',
      keywords: '公用車 通行料 誰が払う,公用車 ETC 精算,官公庁 通行料,一般情報,JTR',
    },
    en: {
      title: 'Who Pays for Government Vehicle Tolls? — Educational Guide | JTR',
      desc: 'A neutral, educational overview of how tolls for official vehicles are generally paid and settled (general information, not legal advice). JTR organizes and compares records — it does not pay tolls. Independent service.',
      keywords: 'who pays government vehicle tolls Japan,official vehicle toll payment,government ETC settlement,educational guide,JTR',
    },
  },
  '/public-safety-fleet-etc-records-japan': {
    ja: {
      title: '公共安全 公用車の ETC 記録管理｜事務的レビュー | JTR',
      desc: '警察・消防・救急・防災などの公共安全組織向けの事務的なETC記録管理。承認車両リストの維持と要確認レビューに対応。捜査・経路・リアルタイム追跡・免除判定は対象外。JTRは独立した民間サービスです。',
      keywords: '公共安全 公用車 ETC,事務的 ETC 記録,承認車両リスト,要確認レビュー,JTR',
    },
    en: {
      title: 'Public-Safety Fleet ETC Records — Administrative Management | JTR',
      desc: 'Administrative ETC record management for authorized public-safety organizations (police, fire, ambulance, emergency-management): approved vehicle lists and needs-review surfacing. No investigations, routes, live tracking, or exemptions. JTR is independent.',
      keywords: 'public-safety fleet ETC records,administrative ETC management,approved vehicle list,needs-review,JTR',
    },
  },

  // ── Feature deep-dive pages (mirror data/featureDetails.js meta) ──
  '/features/daily-etc-reports': {
    ja: {
      title: '日次ETCレポート — 日本の高速道路料金を毎日自動レポート',
      desc: '日本のETC通行料金を毎日自動レポート化。請求書を待たずに前日の利用状況を把握。PDF + CSV、日本語・英語、ETC利用照会サービス（MEISAI）への手動ログイン不要。',
      keywords: '日次ETCレポート,ETC 毎日 レポート,前日 ETC 利用,ETC PDF CSV,ETC利用照会サービス 自動,JTR',
    },
    en: {
      title: 'Daily ETC Reports for Japan — Automatic Daily Toll Activity',
      desc: 'Automatic daily ETC reports for Japan toll roads. See yesterday\'s usage before the card bill arrives. PDF + CSV, bilingual, no manual ETC Usage Inquiry Service (MEISAI) logins.',
      keywords: 'daily ETC reports Japan,automatic daily toll activity,Japan ETC PDF CSV,ETC Usage Inquiry Service automation,JTR',
    },
  },
  '/features/smart-alerts': {
    ja: {
      title: 'ETCスマートアラート — 日本の通行料利用の早期警戒',
      desc: 'ETC利用がルールから外れた瞬間に通知 — 支出しきい値、週末利用、時間外、休眠カード。個人・法人・官公庁対応。',
      keywords: 'ETCスマートアラート,ETC 通知,支出しきい値 ETC,週末利用 ETC,休眠カード ETC,JTR',
    },
    en: {
      title: 'ETC Smart Alerts — Early Warning for Toll Activity in Japan',
      desc: 'Get alerted the moment ETC activity drifts from your rules — spending thresholds, weekend usage, after-hours, dormant card activity. Personal, Business, Government.',
      keywords: 'ETC smart alerts Japan,toll activity early warning,ETC spending threshold,weekend usage alert,dormant card ETC,JTR',
    },
  },
  '/features/authorized-vehicle-registry': {
    ja: {
      title: '登録車両リスト — ETC 利用と承認車両の照合',
      desc: '承認車両リストをアップロード。認可された車両・カード・支店・グループのルール外で利用が発生すると通知。個人ライト版、法人・官公庁フル機能。',
      keywords: '登録車両リスト,承認車両 照合,ETC ルール外 通知,カード 支店 グループ ETC,公用車 ETC,JTR',
    },
    en: {
      title: 'Registered Vehicle Registry — ETC Activity vs Approved Vehicles',
      desc: 'Upload your approved vehicle list. JTR alerts when ETC activity appears outside registered vehicle, card, branch, or group rules. Personal lite, Business + Government full.',
      keywords: 'registered vehicle registry,approved vehicle comparison,ETC activity rules,branch group ETC,public-sector ETC,JTR',
    },
  },
  '/features/reimbursement-reports': {
    ja: {
      title: 'ETC 経費精算・出張精算レポート — 日本対応',
      desc: '出張精算、業務経費、日次運転記録、委託先請求、経理提出に対応した領収書付き ETC レポート。日本語・英語、PDF + CSV。',
      keywords: 'ETC 経費精算 レポート,ETC 出張精算,日次運転記録,委託先請求 ETC,ETC 領収書 PDF CSV,JTR',
    },
    en: {
      title: 'ETC Reimbursement & Travel Claim Reports for Japan',
      desc: 'Receipt-ready ETC reports for travel claims, work reimbursement, daily driving logs, contractor billing, and accounting submissions. Bilingual, PDF + CSV.',
      keywords: 'ETC reimbursement reports Japan,travel claim ETC,driving log report,contractor billing ETC,ETC receipt PDF CSV,JTR',
    },
  },
  '/features/meisai-automation': {
    ja: {
      title: 'ETC利用照会サービス（MEISAI） 自動化 — 手動ログイン不要の ETC データ取得',
      desc: 'JTR が認可された MEISAI 口座に接続し ETC データを自動取得。取得後すぐに配信する方式、ライブデータを継続保存しない設計、日本語・英語レポート。',
      keywords: 'ETC利用照会サービス 自動化,MEISAI 自動取得,手動ログイン不要 ETC,取得後すぐに配信,ETC データ 自動,JTR',
    },
    en: {
      title: 'ETC Usage Inquiry Service (MEISAI) Automation — Automated ETC Data Without Manual Logins',
      desc: 'JTR connects to your authorized MEISAI account and pulls ETC data automatically. Retrieve-and-deliver architecture, zero retention of live data, bilingual reports.',
      keywords: 'ETC Usage Inquiry Service automation,MEISAI automation,automated ETC data,no manual login ETC,retrieve and deliver,JTR',
    },
  },

  // ── Government & official-vehicle toll reference pages (restored) ──
  '/government-military-toll': {
    ja: {
      title: '在日米軍SOFA通行証明：Form 19EJと公用車の通行料の扱い | JTR',
      desc: '日米地位協定（SOFA）第5条第2項に基づく在日米軍の登録車両の通行料の扱い、紙のUSFJ Form 19EJ、USFJI 90-402のアカウンタビリティ要件、ETC専用化で生じる記録のギャップを中立的に解説。JTRは独立した民間サービスです。',
      keywords: 'SOFA 通行証明,USFJ Form 19EJ,在日米軍 公用車 通行料,USFJI 90-402,軍用車両 ETC 記録,JTR',
    },
    en: {
      title: 'US Forces Japan SOFA Toll Certificates: Form 19EJ & Government Vehicle Toll Handling | JTR',
      desc: 'How tolls are handled for US Forces Japan SOFA-registered vehicles under SOFA Article V, ¶2: the paper USFJ Form 19EJ certificate, USFJI 90-402 accountability requirements, and the record-keeping gap created by Japan\'s ETC-only transition. JTR is an independent service.',
      keywords: 'USFJ SOFA toll certificate,USFJ Form 19EJ,US Forces Japan vehicle tolls,USFJI 90-402,military vehicle ETC records Japan,JTR',
    },
  },
  '/government-military-toll-records-japan': {
    ja: {
      title: '日本の官公庁・警察・公用車の通行記録ガイド | JTR',
      desc: '日本の公務の通行は一つの制度ではありません。SOFA軍用車、料金不徴収の公務車両、官庁払いの公用車、混在する車列がそれぞれ異なる根拠に従います。各区分のETC通行記録の整理・照合を中立的に解説。JTRは独立した民間サービスです。',
      keywords: '官公庁 通行記録,公用車 ETC 記録,警察車両 通行料,SOFA 軍用車,公務車両 料金不徴収,JTR',
    },
    en: {
      title: 'Government, Military, Police & Official Vehicle Toll Records in Japan | JTR',
      desc: 'Government toll travel in Japan is not one single system: SOFA military vehicles, non-collected public-duty vehicles, agency-paid official vehicles, and mixed convoys each follow a different basis. A neutral reference for organizing and comparing their ETC toll records. JTR is independent.',
      keywords: 'government toll records Japan,official vehicle ETC records,police vehicle tolls,SOFA military vehicles,public-duty toll non-collection,JTR',
    },
  },
  '/mofa-diplomatic-official-vehicle-tolls-japan': {
    ja: {
      title: '外務省・外交団車両と日本の有料道路の公務移動 | JTR',
      desc: '外務省の公用車移動は通常、官庁払い（ETC・請求・契約）で、自動的に通行料無料ではありません。外交団車両の登録・免税証明はETCカードや通行料免除とは異なり、SOFA／防衛省の制度とも別物です。中立的に整理しました。JTRは独立した民間サービスです。',
      keywords: '外務省 公用車 通行料,外交団車両 ETC,外交ナンバー 通行料,官庁払い ETC,MOFA 通行料,JTR',
    },
    en: {
      title: 'MOFA, Diplomatic Vehicles & Official Toll-Road Travel in Japan | JTR',
      desc: 'MOFA official vehicle travel is generally agency-paid (ETC, invoice, contract), not automatically toll-free. Diplomatic plates and tax-exemption certificates are not an ETC card or toll exemption, and differ from the SOFA / Ministry of Defense system. A neutral reference. JTR is independent.',
      keywords: 'MOFA vehicle tolls Japan,diplomatic vehicle ETC,diplomatic plates toll Japan,agency-paid ETC,MOFA official vehicles,JTR',
    },
  },
  '/japanese-police-fire-emergency-vehicle-toll-rules': {
    ja: {
      title: '日本の警察・消防・救急・緊急車両の通行料ルール | JTR',
      desc: '一部の公務従事車両は、告示第1065号（最新改正：第428号・2026年）により「料金を徴収しない車両」に該当します。該当の可否は車両・公務内容・手続きにより、見た目では決まりません。ETC記録の整理を中立的に解説。JTRは独立した民間サービスです。',
      keywords: '警察車両 通行料,消防車 通行料無料,救急車 ETC,緊急車両 料金不徴収,告示1065号,JTR',
    },
    en: {
      title: 'Japanese Police, Fire, Ambulance & Emergency Vehicle Toll Rules | JTR',
      desc: 'Some Japanese public-duty vehicles qualify as "vehicles for which tolls are not collected" under MLIT Notice No. 1065 (latest amendment No. 428, 2026). Eligibility depends on the vehicle, the duty, and the procedure — not appearance. A neutral reference for organizing ETC records. JTR is independent.',
      keywords: 'Japan police car toll,fire engine toll free Japan,ambulance ETC Japan,emergency vehicle toll non-collection,MLIT Notice 1065,JTR',
    },
  },
  '/japanese-official-convoy-toll-payments': {
    ja: {
      title: '公用車列と警護車両：通行料の負担はどう分かれるのか | JTR',
      desc: '車列は一つの請求カテゴリーではありません。料金の扱いは車両と移動の法的目的に従い、乗員では決まりません。公用車列・要人移動におけるETC通行記録の整理を中立的に解説。JTRは独立した民間サービスです。',
      keywords: '公用車列 通行料,警護車両 ETC,要人移動 通行料,車列 ETC 記録,公用車 通行料 負担,JTR',
    },
    en: {
      title: 'Japanese Official Convoys & Security Vehicles: How Toll Responsibility Is Split | JTR',
      desc: 'A convoy is not one billing category. Toll treatment follows the vehicle and the legal purpose of the trip, not the person being transported. A neutral reference for organizing ETC toll records across official convoys and VIP movements. JTR is independent.',
      keywords: 'official convoy tolls Japan,security vehicle ETC,VIP movement tolls,convoy ETC records,official vehicle toll responsibility,JTR',
    },
  },
  '/diet-members-official-travel-tolls-japan': {
    ja: {
      title: '国会議員の移動・公用車・有料道路の記録 | JTR',
      desc: '国会議員には法定の鉄道・航空手当があり、道路通行料は別問題です。議院・省庁・政党・私用・借上げ・警護のいずれの車両かによって扱いが異なります。ETC通行記録の整理を中立的に解説。JTRは独立した民間サービスです。',
      keywords: '国会議員 移動 通行料,議員 公用車 ETC,議院 車両 通行料,有料道路 記録,国会議員 交通,JTR',
    },
    en: {
      title: 'Japanese Diet Member Travel, Official Cars & Toll-Road Records | JTR',
      desc: 'Diet members have statutory rail/air benefits; road tolls are separate and depend on whether the vehicle is an office, ministry, party, private, hired, or police vehicle. A neutral reference for organizing ETC toll records. JTR is independent.',
      keywords: 'Diet member travel tolls Japan,Diet official car ETC,parliament vehicle tolls,toll road records Japan,Diet member transport,JTR',
    },
  },
  '/business/organization-management': {
    ja: {
      title: '法人向けETCカード管理：組織階層・委任権限・レポートを一元管理 | JTR',
      desc: '法人・複数拠点の組織向けETCカード管理。部署ごとの組織階層、委任権限、読み取り専用の監査ビュー、カード別・車両別・部署別のレポートを一元管理。承認車両との照合と要確認レビューに対応。JTRは独立した民間サービスです。',
      keywords: '法人 ETCカード管理,複数カード・拠点別レポート ETC,委任権限 ETC,部署別 ETC レポート,複数拠点 ETC,JTR',
    },
    en: {
      title: 'Business ETC Card Management: Hierarchy, Delegation & Reports | JTR',
      desc: 'ETC card management for companies and multi-branch fleets: department hierarchy, delegated permissions, read-only auditor views, and reports by card, vehicle, and department — with approved-vehicle comparison and needs-review surfacing. JTR is independent.',
      keywords: 'business ETC card management,multi-card and branch ETC reporting,delegated ETC permissions,department ETC reports,multi-branch ETC,JTR',
    },
  },
  '/government/organization-management': {
    ja: {
      title: '官公庁・公共部門向けETCカード管理：組織階層・委任権限・レポート | JTR',
      desc: '官公庁・公共部門の組織向けETCカード管理。部署ごとの組織階層、委任権限、読み取り専用の監査ビュー、カード別・車両別・部署別のレポートを一元管理。承認車両との照合と要確認レビューに対応。JTRは独立した民間サービスです。',
      keywords: '官公庁 ETCカード管理,公共部門 複数カード・拠点別レポート,委任権限 ETC,部署別 ETC レポート,監査ビュー ETC,JTR',
    },
    en: {
      title: 'Government & Public-Sector ETC Card Management: Hierarchy, Delegation & Reports | JTR',
      desc: 'ETC card management for government and public-sector organizations: department hierarchy, delegated permissions, read-only auditor views, and reports by card, vehicle, and department — with approved-vehicle comparison and needs-review surfacing. JTR is independent.',
      keywords: 'government ETC card management,public-sector multi-card and branch reporting,delegated ETC permissions,department ETC reports,ETC auditor view,JTR',
    },
  },

};

// Pages that should NOT be indexed
// Routes that must NEVER be indexed by search engines.
// robots.txt also blocks crawling, but a leaked direct link still gets
// indexed without `<meta name="robots" content="noindex">`. Belt + braces.
const NOINDEX_PATHS = [
  '/admin',
  '/dashboard',
  '/login',
  '/signin',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  // Legacy internal-artifact paths. If a crawler hits one of these and
  // Cloudflare returns the SPA as a 200 fallback (instead of the 410
  // page wired via vercel.json / _redirects), the SPA must still
  // explicitly tag the response `noindex, nofollow` so the URL never
  // accumulates SEO weight.
  '/exports/',
  '/_phase_b_evidence/',
  '/diag/',
  // Private, non-indexed Swiss & High-Contrast design-system reference
  // build (2026-02) — synthetic content only, must never accumulate SEO
  // weight or appear in search results.
  '/design-preview',
  '/JapanTollReceipts_Japanese_Review_',
  '/JTR_Translation_Bundle',
  '/JTR_Guides_SEO_Audit',
  '/JTR_Sitewide_SEO_Inventory',
  '/JTR_Cloudflare_Bulk_Redirects',
];

export default function SEOHead() {
  const location = useLocation();
  const { lang } = useLanguage();

  useEffect(() => {
    let basePath = location.pathname;
    if (basePath.startsWith('/ja/')) basePath = '/' + basePath.slice(4);
    else if (basePath.startsWith('/en/')) basePath = '/' + basePath.slice(4);
    else if (basePath === '/ja' || basePath === '/en') basePath = '/';

    // Noindex for admin/auth/unlaunched pages — match the raw path AND the
    // language-stripped basePath so /en/* and /ja/* variants are covered too.
    const isNoIndex = NOINDEX_PATHS.some(p => location.pathname.startsWith(p) || basePath.startsWith(p));
    const setMetaTag = (attr, name, content) => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMetaTag('name', 'robots', isNoIndex ? 'noindex, nofollow' : 'index, follow');

    const seo = SEO_DATA[basePath];
    if (!seo) return;
    const rawData = seo[lang] || seo.en || seo.ja;
    if (!rawData) return;

    // Auto-append the brand keyword suffix so every page advertises
    // "ETC利用照会サービス" / "ETC Usage Inquiry Service" / "MEISAI" —
    // see /lib/seoBrand.js for full rationale. Idempotent: no-op when
    // the source string already mentions the brand phrase.
    const isJa = lang === 'ja';
    const data = {
      ...rawData,
      title: withBrandSuffix(rawData.title, isJa),
      desc: withBrandDesc(rawData.desc, isJa),
      keywords: withBrandKeywords(rawData.keywords, isJa),
    };

    document.title = data.title;
    document.documentElement.lang = lang;

    // Active brand domain for canonical + og:url
    const ACTIVE = _activeBrandDomain();

    // Meta tags
    setMetaTag('name', 'description', data.desc);
    if (data.keywords) setMetaTag('name', 'keywords', data.keywords);

    // Open Graph
    setMetaTag('property', 'og:title', data.title);
    setMetaTag('property', 'og:description', data.desc);
    setMetaTag('property', 'og:type', 'website');
    setMetaTag('property', 'og:url', `${ACTIVE}${basePath}`);
    setMetaTag('property', 'og:site_name', 'Japan Toll Receipts');
    setMetaTag('property', 'og:locale', lang === 'ja' ? 'ja_JP' : 'en_US');
    setMetaTag('property', 'og:locale:alternate', lang === 'ja' ? 'en_US' : 'ja_JP');
    setMetaTag('property', 'og:image', `${ACTIVE}${OG_IMAGE}`);

    // Twitter Card
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', data.title);
    setMetaTag('name', 'twitter:description', data.desc);
    setMetaTag('name', 'twitter:image', `${ACTIVE}${OG_IMAGE}`);

    // Canonical URL — strip the /ja/ or /en/ prefix so every language
    // variant of a page resolves to a SINGLE canonical URL. JA pages
    // canonicalize at .jp/<path>; EN pages canonicalize at .jp/en/<path>.
    // .com is never a canonical because it is only an external redirect.
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    const canonicalPath = lang === 'en' ? `/en${basePath === '/' ? '' : basePath}` : basePath;
    canonical.setAttribute('href', `${DOMAIN_JP}${canonicalPath}`);

    // Hreflang alternates — single-domain pattern, .jp serves both
    // languages. Japanese is x-default per Google's "primary market"
    // guidance.
    const setHreflang = (hreflang, href) => {
      let link = document.querySelector(`link[hreflang="${hreflang}"]`);
      if (!link) { link = document.createElement('link'); link.setAttribute('rel', 'alternate'); link.setAttribute('hreflang', hreflang); document.head.appendChild(link); }
      link.setAttribute('href', href);
    };
    setHreflang('ja', `${DOMAIN_JP}${basePath}`);
    setHreflang('en', `${DOMAIN_JP}/en${basePath === '/' ? '' : basePath}`);
    setHreflang('x-default', `${DOMAIN_JP}${basePath}`);

    // JSON-LD structured data
    let jsonLd = document.querySelector('#seo-jsonld');
    if (!jsonLd) { jsonLd = document.createElement('script'); jsonLd.id = 'seo-jsonld'; jsonLd.type = 'application/ld+json'; document.head.appendChild(jsonLd); }

    const ORG_ID = `${ACTIVE}/#organization`;
    const WEBSITE_ID = `${ACTIVE}/#website`;
    const NOJO_ID = `${ACTIVE}/#nojo-enterprise`;
    const HQ_ADDRESS = { "@type": "PostalAddress", "streetAddress": "#202 Takamura Building, 2-2-1 Honcho", "addressLocality": "Yokosuka", "addressRegion": "Kanagawa", "postalCode": "238-0041", "addressCountry": "JP" };
    const graph = [
      {
        // Operating legal entity (corporate profile). Facts mirror the public
        // /corporate-profile + Commercial Transactions Act disclosure pages.
        "@type": "Organization",
        "@id": NOJO_ID,
        "name": "NoJo Enterprise, Co., Ltd.",
        "legalName": "NoJo Enterprise, Co., Ltd.",
        "alternateName": "NoJo Enterprise 株式会社",
        "url": "https://nojoenterprise.com",
        "foundingDate": "2013",
        "telephone": "+81-46-874-7156",
        "email": "support@japantollreceipts.jp",
        "address": HQ_ADDRESS,
        "sameAs": ["https://nojoenterprise.com", "https://nojoenterprise.com/en/services/japantollreceipts"],
      },
      {
        "@type": "Organization",
        "@id": ORG_ID,
        "name": "Japan Toll Receipts",
        "alternateName": ["JapanTollReceipts.jp"],
        "url": ACTIVE,
        "parentOrganization": { "@id": NOJO_ID },
        "sameAs": [DOMAIN_JP, "https://www.facebook.com/JapanTollReceipts", "https://www.instagram.com/japantollreceipts/", `${ACTIVE}/corporate-profile`],
        "telephone": "+81-46-874-7156",
        "email": "support@japantollreceipts.jp",
        "contactPoint": { "@type": "ContactPoint", "telephone": "+81-46-874-7156", "email": "support@japantollreceipts.jp", "contactType": "customer support", "availableLanguage": ["ja", "en"] },
        "address": HQ_ADDRESS,
        "foundingDate": "2013",
        "description": lang === 'ja' ? 'ETC利用照会サービスから取得できる利用明細・利用証明書（PDF/CSV）の取得・整理・メール配信を支援する独立系サポートサービスです。' : 'An independent support service that helps retrieve, organize, and email ETC usage details and certificates available from the ETC Usage Inquiry Service.',
      },
      {
        "@type": "WebSite",
        "@id": WEBSITE_ID,
        "name": "Japan Toll Receipts",
        "url": ACTIVE,
        "publisher": { "@id": ORG_ID },
        "inLanguage": lang === 'ja' ? ['ja', 'en'] : ['en', 'ja'],
        // NOTE: Sitelinks search-box (SearchAction) schema was intentionally
        // removed (2026-05-14 schema-audit pass). Google deprecated the
        // sitelinks search-box rich result; emitting it adds noise without
        // SERP benefit.
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": (() => {
          const homeItem = { "@type": "ListItem", "position": 1, "name": lang === 'ja' ? 'ホーム' : 'Home', "item": ACTIVE };
          if (basePath === '/') return [homeItem];
          // Sprint 2 (2026-05-23) — 3-level breadcrumb for the
          // /guides/* pillar pages and /comparison/* pages:
          // Home → (Guides | Compare) → Page
          if (basePath.startsWith('/guides/')) {
            return [
              homeItem,
              { "@type": "ListItem", "position": 2, "name": lang === 'ja' ? 'ガイド一覧' : 'Guides', "item": `${ACTIVE}/guides` },
              { "@type": "ListItem", "position": 3, "name": data.title.split('|')[0].split('｜')[0].trim(), "item": `${ACTIVE}${basePath}` },
            ];
          }
          if (basePath.startsWith('/comparison/')) {
            return [
              homeItem,
              { "@type": "ListItem", "position": 2, "name": lang === 'ja' ? '比較' : 'Compare', "item": `${ACTIVE}/guides` },
              { "@type": "ListItem", "position": 3, "name": data.title.split('|')[0].split('｜')[0].trim(), "item": `${ACTIVE}${basePath}` },
            ];
          }
          return [
            homeItem,
            { "@type": "ListItem", "position": 2, "name": data.title.split('|')[0].split('｜')[0].trim(), "item": `${ACTIVE}${basePath}` },
          ];
        })(),
      },
    ];

    // Add pricing Offers on pricing page
    if (basePath === '/pricing') {
      graph.push({
        "@type": "Service",
        "name": "Japan Toll Receipts",
        "provider": { "@type": "Organization", "name": "NoJo Enterprise, Co. Ltd." },
        "serviceType": "ETC Toll Report Automation",
        "areaServed": { "@type": "Country", "name": "Japan" },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": lang === 'ja' ? '料金プラン' : 'Pricing Plans',
          "itemListElement": [
            { "@type": "Offer", "name": "Free Weekly", "price": "0", "priceCurrency": "JPY", "description": lang === 'ja' ? '無料週次レポート' : 'Free weekly toll report delivery' },
            { "@type": "Offer", "name": "Daily Plus (Personal)", "price": "300", "priceCurrency": "JPY", "description": lang === 'ja' ? '個人：1枚目¥300＋追加カード¥250（最大10枚・税抜）' : 'Personal: ¥300 first card + ¥250 each additional, up to 10 cards (before tax)' },
            { "@type": "Offer", "name": "Organization (Business / Government)", "price": "1980", "priceCurrency": "JPY", "description": lang === 'ja' ? '組織：月額¥1,980で5枚込み、6〜500枚¥250／501枚〜¥200（最大60,000枚・税抜）' : 'Organization: ¥1,980/mo incl. 5 cards, +¥250 (6–500), +¥200 (501+), up to 60,000 cards (before tax)' },
          ],
        },
      });
    }

    // NOTE: FAQPage schema for /faq is intentionally emitted ONLY by
    // FAQPage.jsx so the structured data exactly matches the visible
    // FAQ items. The previous duplicate hardcoded list here was removed
    // in the 2026-05-14 schema-audit pass.

    // ── Article schema for guide pages (better SERP for long-form) ──
    const GUIDE_PATHS = new Set([
      '/etc-ryoshusho-riyoumeisai-guide',
      '/japan-etc-receipt-guide',
      '/etc-keihi-seisan-report',
      '/japan-toll-expense-reports',
      '/etc-pdf-csv-download-guide',
      '/etc-pdf-csv-report-guide',
      // Iter 264 (2026-05-24) — freee / Money Forward CSV import guide
      '/guides/etc-csv-freee-moneyforward-import-guide',
      // Iter 267 (2026-05-24) — Carshare & rental-car ETC receipts guide
      '/guides/etc-carshare-ryoshusho-guide',
      '/guides/etc-carshare-receipt-guide',
      // Iter 268 (2026-05-24) — Rental-car operator ETC receipt guide
      '/guides/rental-car-company-etc-receipt-guide',
      '/etc-phishing-safety',
      '/etc-riyou-rireki-printer-guide',
      '/etc-usage-history-printer-guide',
      '/legal-etc-setup',
      '/guides/etc-card-number-formats',
      // Sprint 2 (2026-05-23) — Japanese ETC利用明細 pillar
      '/guides/etc-riyou-meisai-kanzen-guide',
      '/guides/etc-usage-statement-complete-guide',
      // Sprint 3 (2026-05-23) — Japanese ETC明細 反映遅延 pillar
      '/guides/etc-meisai-han-ei-osoi-riyu',
      '/guides/etc-statement-delay-guide',
      // Sprint 4 (2026-05-23) — ETC利用照会サービス vs JTR comparison
      '/comparison/etc-riyou-shoukai-vs-jtr',
      '/comparison/etc-inquiry-service-vs-jtr',
      // Sprint 5 (2026-05-23) — 法人ETC明細管理 commercial pillar
      '/guides/houjin-etc-meisai-kanri',
      '/guides/corporate-etc-statement-management',
      // Iter 281 (2026-05-24) — ETC利用証明書とETC領収書の違い dedicated guide
      '/guides/etc-riyou-shoumeisho-ryoshusho-chigai-guide',
      '/guides/etc-usage-certificate-vs-receipt-difference-guide',
      // Iter 282 (2026-05-24) — ETC再セットアップ / セットアップ証明書 workflow guide
      '/guides/etc-resetup-setup-certificate-guide',
      '/guides/etc-resetup-setup-certificate-en-guide',
      // Iter 283 (2026-05-24) — ETCマイレージ login / registration troubleshooting guide
      '/guides/etc-mileage-login-registration-troubleshooting-guide',
      '/guides/etc-mileage-login-registration-troubleshooting-en-guide',
      // Iter 284 (2026-05-24) — ETC利用照会サービス registration walkthrough
      '/guides/etc-riyou-shoukai-service-registration-guide',
      '/guides/etc-usage-inquiry-service-registration-guide',
      // Wave 1 / Iter 285-288 (2026-05-24) — accounting / retention cluster
      '/guides/etc-meisai-hozon-kikan-guide',
      '/guides/etc-statement-retention-period-guide',
      '/guides/koutsuhi-shouhizei-etc-meisai-guide',
      '/guides/highway-toll-consumption-tax-etc-statement-guide',
      '/guides/koutsuhi-shiwake-etc-meisai-guide',
      '/guides/highway-toll-journal-entry-etc-statement-guide',
      '/guides/etc-meisai-denshi-chobo-hozon-hou-guide',
      '/guides/etc-statement-e-storage-law-guide',
      // Wave 2 / Iter 289 (2026-05-24) — ETC明細 表示されない + 過去分 (combined)
      '/guides/etc-meisai-hyoji-sarenai-kako-guide',
      '/guides/etc-statement-not-showing-historical-guide',
      // Wave 6 / Iter 290 (2026-05-24) — 経費精算スターターキット cluster-hub
      '/guides/etc-keihi-seisan-starter-kit-guide',
      '/guides/etc-expense-report-starter-kit-guide',
    ]);
    if (GUIDE_PATHS.has(basePath)) {
      graph.push({
        "@type": "Article",
        "headline": data.title,
        "description": data.desc,
        "inLanguage": lang === 'ja' ? 'ja-JP' : 'en-US',
        "mainEntityOfPage": { "@type": "WebPage", "@id": `${ACTIVE}${basePath}` },
        "image": `${ACTIVE}${OG_IMAGE}`,
        "author": { "@type": "Organization", "name": "Japan Toll Receipts" },
        "publisher": {
          "@type": "Organization",
          "name": "NoJo Enterprise, Co. Ltd.",
          "logo": { "@type": "ImageObject", "url": `${ACTIVE}${OG_IMAGE}` },
        },
        "datePublished": (basePath.startsWith('/guides/etc-riyou-meisai-kanzen-guide')
          || basePath.startsWith('/guides/etc-usage-statement-complete-guide')
          || basePath.startsWith('/guides/etc-meisai-han-ei-osoi-riyu')
          || basePath.startsWith('/guides/etc-statement-delay-guide')
          || basePath.startsWith('/comparison/etc-riyou-shoukai-vs-jtr')
          || basePath.startsWith('/comparison/etc-inquiry-service-vs-jtr')
          || basePath.startsWith('/guides/houjin-etc-meisai-kanri')
          || basePath.startsWith('/guides/corporate-etc-statement-management'))
          ? "2026-05-23"
          : (basePath.startsWith('/guides/etc-riyou-shoumeisho-ryoshusho-chigai-guide')
            || basePath.startsWith('/guides/etc-usage-certificate-vs-receipt-difference-guide')
            || basePath.startsWith('/guides/etc-resetup-setup-certificate-guide')
            || basePath.startsWith('/guides/etc-resetup-setup-certificate-en-guide')
            || basePath.startsWith('/guides/etc-mileage-login-registration-troubleshooting-guide')
            || basePath.startsWith('/guides/etc-mileage-login-registration-troubleshooting-en-guide')
            || basePath.startsWith('/guides/etc-riyou-shoukai-service-registration-guide')
            || basePath.startsWith('/guides/etc-usage-inquiry-service-registration-guide')
            || basePath.startsWith('/guides/etc-meisai-hozon-kikan-guide')
            || basePath.startsWith('/guides/etc-statement-retention-period-guide')
            || basePath.startsWith('/guides/koutsuhi-shouhizei-etc-meisai-guide')
            || basePath.startsWith('/guides/highway-toll-consumption-tax-etc-statement-guide')
            || basePath.startsWith('/guides/koutsuhi-shiwake-etc-meisai-guide')
            || basePath.startsWith('/guides/highway-toll-journal-entry-etc-statement-guide')
            || basePath.startsWith('/guides/etc-meisai-denshi-chobo-hozon-hou-guide')
            || basePath.startsWith('/guides/etc-statement-e-storage-law-guide')
            || basePath.startsWith('/guides/etc-meisai-hyoji-sarenai-kako-guide')
            || basePath.startsWith('/guides/etc-statement-not-showing-historical-guide')
            || basePath.startsWith('/guides/etc-keihi-seisan-starter-kit-guide')
            || basePath.startsWith('/guides/etc-expense-report-starter-kit-guide'))
            ? "2026-05-24"
            : "2026-05-01",
        "dateModified": (basePath.startsWith('/guides/etc-riyou-meisai-kanzen-guide')
          || basePath.startsWith('/guides/etc-usage-statement-complete-guide')
          || basePath.startsWith('/guides/etc-meisai-han-ei-osoi-riyu')
          || basePath.startsWith('/guides/etc-statement-delay-guide')
          || basePath.startsWith('/comparison/etc-riyou-shoukai-vs-jtr')
          || basePath.startsWith('/comparison/etc-inquiry-service-vs-jtr')
          || basePath.startsWith('/guides/houjin-etc-meisai-kanri')
          || basePath.startsWith('/guides/corporate-etc-statement-management'))
          ? "2026-05-23"
          : (basePath.startsWith('/guides/etc-riyou-shoumeisho-ryoshusho-chigai-guide')
            || basePath.startsWith('/guides/etc-usage-certificate-vs-receipt-difference-guide')
            || basePath.startsWith('/guides/etc-resetup-setup-certificate-guide')
            || basePath.startsWith('/guides/etc-resetup-setup-certificate-en-guide')
            || basePath.startsWith('/guides/etc-mileage-login-registration-troubleshooting-guide')
            || basePath.startsWith('/guides/etc-mileage-login-registration-troubleshooting-en-guide')
            || basePath.startsWith('/guides/etc-riyou-shoukai-service-registration-guide')
            || basePath.startsWith('/guides/etc-usage-inquiry-service-registration-guide')
            || basePath.startsWith('/guides/etc-meisai-hozon-kikan-guide')
            || basePath.startsWith('/guides/etc-statement-retention-period-guide')
            || basePath.startsWith('/guides/koutsuhi-shouhizei-etc-meisai-guide')
            || basePath.startsWith('/guides/highway-toll-consumption-tax-etc-statement-guide')
            || basePath.startsWith('/guides/koutsuhi-shiwake-etc-meisai-guide')
            || basePath.startsWith('/guides/highway-toll-journal-entry-etc-statement-guide')
            || basePath.startsWith('/guides/etc-meisai-denshi-chobo-hozon-hou-guide')
            || basePath.startsWith('/guides/etc-statement-e-storage-law-guide')
            || basePath.startsWith('/guides/etc-meisai-hyoji-sarenai-kako-guide')
            || basePath.startsWith('/guides/etc-statement-not-showing-historical-guide')
            || basePath.startsWith('/guides/etc-keihi-seisan-starter-kit-guide')
            || basePath.startsWith('/guides/etc-expense-report-starter-kit-guide'))
            ? "2026-05-24"
            : "2026-05-10",
      });
    }

    jsonLd.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
  }, [location.pathname, lang]);

  return null;
}
